import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import EditorLayout from "../../layouts/EditorLayout";
import EditorInput from "../../components/editor/inputs/EditorInput";
import usePageTitle from "../../utils/hooks/usePageTitle";

import {
  ProgramType,
  NotificationMethods,
  ProgramTarget
} from "../../utils/api/endpoints/programs/parsePrograms";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { useGetAllUsersQuery } from "../../utils/api/endpoints/usersApi";
import EditorSection from "../../components/editor/EditorSection";
// Targets are now derived from selected assets; manual TargetsInput removed
import DropDownEditor from "../../components/editor/inputs/programs/DropDownEditor";
import DatePicker from "../../components/editor/inputs/programs/DatePicker";
import EditorRichTextInput from "../../components/editor/inputs/EditorRichTextInput";
import ProgramRewardsInput from "../../components/editor/inputs/programs/ProgramRewardsInput";
import ProgramTargetsEditor from "../../components/editor/inputs/programs/ProgramTargetsEditor";

import useProgram, {
  ProgramUpdateValues
} from "../../utils/hooks/programs/useProgram";
import EditorTextInput from "../../components/editor/inputs/EditorTextInput";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  testingTypeOptions,
  environmentTypeOptions,
  complianceTypeOptions,
  complianceTypes
} from "../../utils/constants";

import FormDropdown from "../../components/forms/inputs/FormDropdown";
import { FiHelpCircle } from "react-icons/fi";
import { FaSlack } from "react-icons/fa";
import { SiJira } from "react-icons/si";
import JiraSetupGuideModal from "../../components/modals/JiraSetupGuideModal";

import ProgramEditorLogo from "../../components/editor/inputs/ProgramEditorLogo";
import ProgramEditorAttachments from "../../components/editor/inputs/ProgramEditorAttachments";
// react-select no longer needed for asset selection - using ProgramAssetSelector
// New imports for auto-save and error handling
import ProgramEditorErrorBoundary from "../../components/error/ProgramEditorErrorBoundary";
import DraftRecoveryModal from "../../components/editor/DraftRecoveryModal";
import EditorTopBar from "../../components/editor/EditorTopBar";
import { useFormContext, FormProvider, useForm, Controller, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import storageManager from "../../utils/storage/localStorageManager";
import {
  getCompanyById,
  getMyCompany,
  updateMyCompanyDetails,
  Company,
  listCompanyNamesAdmin,
  resolveCompanyByNameAdmin,
  updateCompanyDetailsAdmin,
} from "../../utils/api/endpoints/companies";
import { FormInput, FormSelect, FormTextarea, FormDateInput, IntegrationToggle, StepNavigation } from "../../components/forms";
import { VaultSelector, VaultPreSelector } from "../../components/vault";
import type { VaultLinkConfig } from "../../components/vault/VaultPreSelector";
import { linkVaultToProgram } from "../../utils/api/endpoints/vault";



// Configuration constants for file handling and performance
const MAX_FILE_SIZE = 250 * 1024 * 1024; // 250MB per file
const MAX_TOTAL_FILES_SIZE = 500 * 1024 * 1024; // 500MB total
const MAX_FILES_COUNT = 10; // Maximum number of files
const MAX_FORM_DATA_SIZE = 5 * 1024 * 1024; // 5MB max form data size
const PERFORMANCE_THROTTLE_SIZE = 1 * 1024 * 1024; // 1MB - start throttling above this


// Utility function to validate file size and count
const validateFiles = (files: File[]): { valid: boolean; error?: string; totalSize: number } => {
  if (!files || files.length === 0) {
    return { valid: true, totalSize: 0 };
  }

  if (files.length > MAX_FILES_COUNT) {
    return {
      valid: false,
      error: `Too many files. Maximum ${MAX_FILES_COUNT} files allowed.`,
      totalSize: 0
    };
  }

  let totalSize = 0;
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File "${file.name}" is too large. Maximum size is ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB.`,
        totalSize: 0
      };
    }
    totalSize += file.size;
  }

  if (totalSize > MAX_TOTAL_FILES_SIZE) {
    return {
      valid: false,
      error: `Total file size too large. Maximum ${Math.round(MAX_TOTAL_FILES_SIZE / 1024 / 1024)}MB allowed.`,
      totalSize
    };
  }

  return { valid: true, totalSize };
};

// Utility function to prepare data for saving (serialize File objects and dates)
const prepareDataForSaving = (data: any): { prepared: any; warnings: string[] } => {
  if (!data || typeof data !== 'object') {
    return { prepared: data, warnings: [] };
  }

  const prepared = { ...data };
  const warnings: string[] = [];

  // Exclude sensitive fields from being stored in drafts
  const sensitiveFields = ['jira_api_token', 'jira_email', 'vpn', 'credentials'];
  sensitiveFields.forEach((field) => {
    if (field in prepared) {
      delete prepared[field as keyof typeof prepared];
    }
  });

  // Handle File objects (logo/attachments) - convert to metadata for storage
  if (prepared.profilePicture && Array.isArray(prepared.profilePicture)) {
    const files = prepared.profilePicture.filter((item: any) => item instanceof File);
    const validation = validateFiles(files);

    if (!validation.valid) {
      warnings.push(`Profile picture: ${validation.error}`);
      // Keep only non-File items (existing URLs)
      prepared.profilePicture = prepared.profilePicture.filter((item: any) => !(item instanceof File));
    } else {
      prepared.profilePicture = prepared.profilePicture.map((file: any) => {
        if (file instanceof File) {
          return {
            _isFile: true,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          };
        }
        return file;
      });
    }
  }

  if (prepared.attachments && Array.isArray(prepared.attachments)) {
    const files = prepared.attachments.filter((item: any) => item instanceof File);
    const validation = validateFiles(files);

    if (!validation.valid) {
      warnings.push(`Attachments: ${validation.error}`);
      // Keep only non-File items (existing URLs)
      prepared.attachments = prepared.attachments.filter((item: any) => !(item instanceof File));
    } else {
      prepared.attachments = prepared.attachments.map((file: any) => {
        if (file instanceof File) {
          return {
            _isFile: true,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified
          };
        }
        return file;
      });
    }
  }

  // Handle Date objects - convert to ISO strings for storage
  const dateFields = [
    'expectedStartDate',
    'expectedEndDate',
    'start_date',
    'end_date',
    'created_at',
    'updated_at',
    'deadline',
    'launch_date',
    'current_date',
    'revision_date',
    'date_of_request'
  ];

  dateFields.forEach(field => {
    if (prepared[field] instanceof Date) {
      prepared[field] = {
        _isDate: true,
        value: prepared[field].toISOString()
      };
    }
  });

  // Recursively handle nested objects
  Object.keys(prepared).forEach(key => {
    if (prepared[key] && typeof prepared[key] === 'object' && !Array.isArray(prepared[key]) && !(prepared[key] instanceof Date) && !(prepared[key] instanceof File)) {
      const result = prepareDataForSaving(prepared[key]);
      prepared[key] = result.prepared;
      warnings.push(...result.warnings);
    } else if (Array.isArray(prepared[key])) {
      prepared[key] = prepared[key].map((item: any) => {
        if (typeof item === 'object' && !(item instanceof File) && !(item instanceof Date)) {
          const result = prepareDataForSaving(item);
          warnings.push(...result.warnings);
          return result.prepared;
        }
        return item;
      });
    }
  });

  return { prepared, warnings };
};

// Utility function to restore data from saved draft (restore File metadata and dates)
const restoreDataFromSaved = (data: any): any => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const restored = { ...data };

  // Restore Date objects from saved format
  const dateFields = [
    'expectedStartDate',
    'expectedEndDate',
    'start_date',
    'end_date',
    'created_at',
    'updated_at',
    'deadline',
    'launch_date',
    'current_date',
    'revision_date',
    'date_of_request'
  ];

  dateFields.forEach(field => {
    if (restored[field] && typeof restored[field] === 'object' && restored[field]._isDate) {
      try {
        const date = new Date(restored[field].value);
        if (!isNaN(date.getTime())) {
          restored[field] = date;
        } else {
          console.warn(`Invalid saved date in field ${field}:`, restored[field].value);
          restored[field] = null;
        }
      } catch (error) {
        console.warn(`Error restoring date in field ${field}:`, error);
        restored[field] = null;
      }
    } else if (restored[field] && typeof restored[field] === 'string') {
      // Handle legacy date strings
      try {
        const date = new Date(restored[field]);
        if (!isNaN(date.getTime())) {
          // For form fields that expect Date objects
          if (field === 'expectedStartDate' || field === 'expectedEndDate') {
            restored[field] = date;
          }
        }
      } catch (error) {
        console.warn(`Error parsing legacy date in field ${field}:`, error);
      }
    }
  });


  // Recursively restore nested objects
  Object.keys(restored).forEach(key => {
    if (restored[key] && typeof restored[key] === 'object' && !Array.isArray(restored[key]) && !(restored[key] instanceof Date)) {
      restored[key] = restoreDataFromSaved(restored[key]);
    } else if (Array.isArray(restored[key])) {
      restored[key] = restored[key].map((item: any) =>
        typeof item === 'object' && !item._isFile && !item._isDate ? restoreDataFromSaved(item) : item
      );
    }
  });

  return restored;
};

// Define CSS keyframe animations using a style element
const AnimationStyles = () => (
  <style>
    {`
      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.02);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
      }
      
      @keyframes checkmark-appear {
        0% {
          transform: scale(0) rotate(-45deg);
          opacity: 0;
        }
        100% {
          transform: scale(1) rotate(-45deg);
          opacity: 1;
        }
      }
      
      @keyframes slide-in {
        0% {
          transform: translateX(16px);
        }
        100% {
          transform: translateX(0);
        }
      }
    `}
  </style>
);

// Top stepper header for create flow
const StepperHeader = ({ activeStep, skipCompany, isAdmin }: { activeStep: number; skipCompany?: boolean; isAdmin?: boolean }) => {
  const steps: string[] = [];
  if (isAdmin) steps.push("Select Company");
  if (!skipCompany) steps.push("Company Details");
  steps.push("Program Details", "Asset Details", "Testing Details", "Review & Submit");

  return <StepNavigation steps={steps} activeStep={activeStep} skipCompany={skipCompany} />;
};

// Step 2: Program Details section
const ProgramDetailsStep = ({
  onNext,
  onPrevious,
  slackSelected,
  setSlackSelected,
  jiraSelected,
  setJiraSelected,
  notificationMethods,
  setNotificationMethods
}: {
  onNext: () => void;
  onPrevious: () => void;
  slackSelected: boolean;
  setSlackSelected: (value: boolean) => void;
  jiraSelected: boolean;
  setJiraSelected: (value: boolean) => void;
  notificationMethods: String[];
  setNotificationMethods: React.Dispatch<React.SetStateAction<String[]>>;
}) => {
  const { setValue, getValues, register, watch } = useFormContext();
  const [showJiraGuide, setShowJiraGuide] = useState(false);
  const [jiraTestResult, setJiraTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // Use local state for inputs to avoid re-render issues
  const [programName, setProgramName] = useState('');
  const [programType, setProgramType] = useState('');
  // Removed local state for scope/description; bind directly to RHF
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [jiraUrl, setJiraUrl] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraApiToken, setJiraApiToken] = useState('');
  const [jiraProjectKey, setJiraProjectKey] = useState('');

  // Strip HTML tags once for initial/default values
  const stripHtml = (html: string) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Watch form values reactively to keep local state in sync
  const watchedTitle = watch('title');
  const watchedType = watch('type');
  const watchedScope = watch('scope');
  const watchedOutOfScope = watch('outOfScope');
  const watchedKnownVulnerabilities = watch('knownVulnerabilities');
  const watchedDescription = watch('description');
  const watchedSlackChannelLink = watch('slack_channel_link');
  const watchedJiraUrl = watch('jira_url');
  const watchedJiraEmail = watch('jira_email');
  const watchedJiraApiToken = watch('jira_api_token');
  const watchedJiraProjectKey = watch('jira_project_key');

  // Update local state when watched values change
  useEffect(() => {
    if (watchedTitle !== undefined) setProgramName(watchedTitle || '');
  }, [watchedTitle]);

  useEffect(() => {
    if (watchedType !== undefined) setProgramType(watchedType || '');
  }, [watchedType]);

  useEffect(() => {
    if (watchedSlackChannelLink !== undefined) setSlackWebhookUrl(watchedSlackChannelLink || '');
  }, [watchedSlackChannelLink]);

  useEffect(() => {
    if (watchedJiraUrl !== undefined) setJiraUrl(watchedJiraUrl || '');
  }, [watchedJiraUrl]);

  useEffect(() => {
    if (watchedJiraEmail !== undefined) setJiraEmail(watchedJiraEmail || '');
  }, [watchedJiraEmail]);

  useEffect(() => {
    if (watchedJiraApiToken !== undefined) setJiraApiToken(watchedJiraApiToken || '');
  }, [watchedJiraApiToken]);

  useEffect(() => {
    if (watchedJiraProjectKey !== undefined) setJiraProjectKey(watchedJiraProjectKey || '');
  }, [watchedJiraProjectKey]);

  // Clean HTML from scope and description when they change
  useEffect(() => {
    if (watchedScope !== undefined) {
      const cleanedScope = stripHtml(watchedScope || '');
      if (cleanedScope !== watchedScope) {
        setValue('scope', cleanedScope, { shouldDirty: false });
      }
    }
  }, [watchedScope, setValue]);

  useEffect(() => {
    if (watchedDescription !== undefined) {
      const cleanedDesc = stripHtml(watchedDescription || '');
      if (cleanedDesc !== watchedDescription) {
        setValue('description', cleanedDesc, { shouldDirty: false });
      }
    }
  }, [watchedDescription, setValue]);

  // Remove debounce sync – we'll bind fields directly to form

  const handleChange = (field: string, value: string, localSetter: (v: string) => void) => {
    localSetter(value);
    setValue(field, value);
  };

  const handleToggleSlack = () => {
    const newValue = !slackSelected;
    setSlackSelected(newValue);

    setNotificationMethods(prevMethods => {
      const methods = [...prevMethods] as String[];
      if (!methods.includes(NotificationMethods.CTB)) {
        methods.push(NotificationMethods.CTB);
      }
      if (newValue && !methods.includes(NotificationMethods.SLACK)) {
        methods.push(NotificationMethods.SLACK);
      } else if (!newValue) {
        return methods.filter(m => m !== NotificationMethods.SLACK) as String[];
      }
      return methods;
    });
  };

  const handleToggleJira = () => {
    const newValue = !jiraSelected;
    setJiraSelected(newValue);

    setNotificationMethods(prevMethods => {
      const methods = [...prevMethods] as String[];
      if (!methods.includes(NotificationMethods.CTB)) {
        methods.push(NotificationMethods.CTB);
      }
      if (newValue && !methods.includes(NotificationMethods.JIRA)) {
        methods.push(NotificationMethods.JIRA);
      } else if (!newValue) {
        return methods.filter(m => m !== NotificationMethods.JIRA) as String[];
      }
      return methods;
    });
  };

  const testJiraConnection = async () => {
    setTesting(true);
    setJiraTestResult(null);

    try {
      const config = {
        url: jiraUrl,
        email: jiraEmail,
        api_token: jiraApiToken,
        project_key: jiraProjectKey
      };

      // Validate all fields are provided
      if (!config.url || !config.email || !config.api_token || !config.project_key) {
        setJiraTestResult({
          success: false,
          error: "Please fill in all JIRA configuration fields before testing"
        });
        setTesting(false);
        return;
      }

      // Import axios instance
      const axios = (await import("../../utils/api/axios")).default;
      const response = await axios.post("/v2/programs/jira/validate", config);
      setJiraTestResult(response.data);
    } catch (error: any) {
      let errorMessage = "Failed to connect to server";
      let suggestions = ["Please check your internet connection and try again"];

      if (error.response?.status === 401) {
        errorMessage = "Authentication failed - Please log in again";
        suggestions = ["Try refreshing the page and logging in again"];
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied - Insufficient permissions";
        suggestions = ["Contact your administrator for access to JIRA testing"];
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        suggestions = error.response.data.suggestions || suggestions;
      }

      setJiraTestResult({
        success: false,
        error: errorMessage,
        suggestions: suggestions
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="w-full rounded-b-xl border border-t-0 border-gray-200 bg-white shadow-lg overflow-hidden mb-6">
      <div className="p-6 sm:p-8 lg:p-10">
        <h2 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Program Details</h2>
        <p className="mb-6 text-sm sm:text-base text-slate-600">Define the security program you want to create</p>

        <div className="space-y-6">
          {/* Program Name */}
          <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
            <FormInput
              name="title"
              label="Program Name"
              value={programName}
              onChange={(value) => handleChange('title', value, setProgramName)}
              placeholder="Acme Security Assessment Program"
              required={true}
            />

            <FormSelect
              name="type"
              label="Program Type"
              options={[
                { value: ProgramType.PTAAS, label: "PTaaS" },
                { value: ProgramType.VDP, label: "VDP" },
                { value: ProgramType.BugBounty, label: "Bug Bounty" }
              ]}
              placeholder="Select Program Type"
              value={programType}
              onChange={(value) => handleChange('type', value, setProgramType)}
              required={true}
              description="Select the program's approach for researchers to process vulnerabilities."
            />
          </div>

          <FormTextarea
            name="scope"
            label="Program Scope"
            placeholder="Describe the scope of your security program..."
            rows={4}
            description="For a thorough understanding of the testing scope and assets, please provide the necessary documentation and any relevant walkthrough video links"
            value={useWatch({ name: 'scope' }) || ''}
            onChange={(value) => setValue('scope', value)}
            required={true}
          />

          {/* Out of Scope - Required for VDP and Bug Bounty, hidden for PTAAS */}
          {(programType === ProgramType.VDP || programType === ProgramType.BugBounty) && (
            <FormTextarea
              name="outOfScope"
              label="Out of Scope"
              placeholder="Describe what is NOT in scope for this program..."
              rows={4}
              description="Provide details about the digital assets that are NOT open for examination and reporting."
              value={watchedOutOfScope || ''}
              onChange={(value) => setValue('outOfScope', value)}
              required={true}
            />
          )}

          {/* Known Vulnerabilities - Optional for VDP and Bug Bounty, hidden for PTAAS */}
          {(programType === ProgramType.VDP || programType === ProgramType.BugBounty) && (
            <FormTextarea
              name="knownVulnerabilities"
              label="Known Vulnerabilities"
              placeholder="Provide details about any addressed vulnerabilities..."
              rows={4}
              description="Provide details about any addressed vulnerabilities (from previous experience or approved reports)"
              value={watchedKnownVulnerabilities || ''}
              onChange={(value) => setValue('knownVulnerabilities', value)}
              required={false}
            />
          )}

          {/* Bug Bounty specific fields */}
          {programType === ProgramType.BugBounty && (
            <>
              {/* Program Access */}
              <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-slate-700">Program Access <span className="text-red-500">*</span></label>
                  <Controller
                    name="private"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <select
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <option value={0}>Public</option>
                        <option value={1}>Private</option>
                      </select>
                    )}
                  />
                  <p className="details mt-1 text-xs text-gray-600">Select the visibility and access type of this program.</p>
                </div>
              </div>

              {/* Reward Tiers */}
              <div className="mt-6">
                <ProgramRewardsInput name="rewards" inputTitle="Reward Tiers" required />
              </div>

              {/* Reward Policy */}
              <div className="mt-6 flex flex-col">
                <label className="mb-2 text-sm font-medium text-slate-700">Reward Policy <span className="text-red-500">*</span></label>
                <Controller
                  name="rewardPolicy"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <textarea
                      className="w-full h-32 rounded-lg border border-gray-300 px-3 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                      placeholder="Provide rules and guidelines outlining how researchers are compensated for responsibly disclosing vulnerabilities..."
                      rows={4}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>

              {/* Terms and Conditions */}
              <div className="mt-6 flex flex-col">
                <label className="mb-2 text-sm font-medium text-slate-700">Terms and Conditions <span className="text-red-500">*</span></label>
                <Controller
                  name="termsOfService"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <textarea
                      className="w-full h-32 rounded-lg border border-gray-300 px-3 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                      placeholder="Provide an outline of the terms and conditions of how this program will be conducted..."
                      rows={4}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
            </>
          )}

          {/* App Integrations */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-slate-900">
              App Integrations
            </label>
            <div className="grid grid-cols-2">
              <IntegrationToggle
                name="slack"
                label="Slack"
                isSelected={slackSelected}
                onToggle={handleToggleSlack}
                className="rounded-l-lg"
                showDivider={slackSelected && jiraSelected}
              />
              <IntegrationToggle
                name="jira"
                label="Jira"
                isSelected={jiraSelected}
                onToggle={handleToggleJira}
                className="rounded-r-lg"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              You can select multiple integrations for your program. Each integration requires specific configuration.
            </p>
          </div>

          {/* Slack Configuration - Shows when Slack is selected */}
          {slackSelected && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#010D2C] text-white">
                  <FaSlack className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Slack Configuration</h3>
                  <p className="text-xs text-slate-600">Configure notifications for your Slack workspace</p>
                </div>
              </div>

              {/* Integration Guide */}
              <div className="rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-green-900">Integration Guide</h4>
                    <p className="mt-1 text-xs text-gray-700">
                      To integrate with Slack, you'll need to create an incoming webhook for your channel. Visit the{' '}
                      <a
                        href="https://api.slack.com/messaging/webhooks"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-green-700 underline hover:text-green-900"
                      >
                        Slack API documentation
                      </a>{' '}
                      to learn how.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-slate-700">
                  Slack Webhook URL {slackSelected && <span className="text-red-500">*</span>}
                </label>
                <input
                  className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                  value={slackWebhookUrl}
                  onChange={(e) => handleChange('slack_channel_link', e.target.value, setSlackWebhookUrl)}
                  placeholder="https://hooks.slack.com/services/XXXXX/XXXXX/XXXXX"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Enter your Slack webhook URL to receive notifications
                </p>
              </div>
            </div>
          )}

          {/* Jira Configuration - Shows when Jira is selected */}
          {jiraSelected && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
                    <SiJira className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Jira Configuration</h3>
                    <p className="text-xs text-slate-600">Configure Jira integration for issue tracking</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowJiraGuide(true)}
                  className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Setup Guide
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-slate-700">
                    Jira Site URL {jiraSelected && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    value={jiraUrl}
                    onChange={(e) => handleChange('jira_url', e.target.value, setJiraUrl)}
                    placeholder="https://yourdomain.atlassian.net"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    The base URL for your Jira site
                  </p>
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-slate-700">
                    Jira Email {jiraSelected && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="email"
                    className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    value={jiraEmail}
                    onChange={(e) => handleChange('jira_email', e.target.value, setJiraEmail)}
                    placeholder="email@example.com"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    The email associated with your Jira account
                  </p>
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-slate-700">
                    Jira API Token {jiraSelected && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    value={jiraApiToken}
                    onChange={(e) => handleChange('jira_api_token', e.target.value, setJiraApiToken)}
                    placeholder="Enter your Jira API token"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Your Jira API token for authentication
                  </p>
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 text-sm font-medium text-slate-700">
                    Jira Project Key {jiraSelected && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    value={jiraProjectKey}
                    onChange={(e) => handleChange('jira_project_key', e.target.value, setJiraProjectKey)}
                    placeholder="e.g., PROJECT"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    The project key for your Jira project
                  </p>
                </div>
              </div>

              {/* Test Connection Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-900">Connection Test</h4>
                  <button
                    type="button"
                    onClick={testJiraConnection}
                    disabled={testing}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${testing
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                  >
                    {testing ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Testing...
                      </>
                    ) : (
                      "Test Connection"
                    )}
                  </button>
                </div>

                {/* Test Results */}
                {jiraTestResult && (
                  <div
                    className={`rounded-lg border p-4 ${jiraTestResult.success
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={jiraTestResult.success ? "text-green-500" : "text-red-500"}>
                        {jiraTestResult.success ? (
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className={`text-sm font-semibold ${jiraTestResult.success ? "text-green-800" : "text-red-800"}`}>
                          {jiraTestResult.success ? "Connection Successful!" : "Connection Failed"}
                        </h5>
                        {jiraTestResult.success ? (
                          <div className="mt-2">
                            {jiraTestResult.projectInfo && (
                              <div className="text-xs text-green-700 space-y-1">
                                <p>
                                  <strong>Project:</strong> {jiraTestResult.projectInfo.name} ({jiraTestResult.projectInfo.key})
                                </p>
                                <p>
                                  <strong>Available Issue Types:</strong> {jiraTestResult.projectInfo.issueTypes.join(", ")}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-2">
                            <p className="text-xs text-red-700">{jiraTestResult.error}</p>
                            {jiraTestResult.suggestions && jiraTestResult.suggestions.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-red-700">Suggestions:</p>
                                <ul className="mt-1 list-disc list-inside text-xs text-red-600 space-y-0.5">
                                  {jiraTestResult.suggestions.map((suggestion: string, index: number) => (
                                    <li key={index}>{suggestion}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Program Rules / Description */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-slate-700">
              Program Rules / Description <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-3 text-gray-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <Controller
                name="description"
                render={({ field }) => (
                  <textarea
                    className="w-full h-32 rounded-lg border border-gray-300 px-3 py-3 pl-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    placeholder="Specify the rules, guidelines, and expectations for this security program..."
                    rows={4}
                    value={field.value || ''}
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
            <p className="details mt-1 text-xs text-[#64748B]">Describe the objectives, what this pentest program is about and expected outcomes of this security testing program</p>
          </div>

          {/* Terms and Conditions for VDP */}
          {programType === ProgramType.VDP && (
            <div className="flex flex-col">
              <label className="mb-2 text-sm font-medium text-slate-700">
                Terms and Conditions <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <Controller
                  name="termsOfService"
                  render={({ field }) => (
                    <textarea
                      className="w-full h-32 rounded-lg border border-gray-300 px-3 py-3 pl-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                      placeholder="Provide an outline of the terms and conditions of how this program will be conducted..."
                      rows={4}
                      value={field.value || ''}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="px-6 sm:px-8 lg:px-10 py-4 border-t border-gray-200 flex items-center justify-between" style={{ backgroundColor: '#F8FAFC' }}>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          type="button"
          onClick={onPrevious}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>
        <button
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          onClick={onNext}
          type="button"
        >
          Next
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Jira Setup Guide Modal */}
      <JiraSetupGuideModal
        isOpen={showJiraGuide}
        onClose={() => setShowJiraGuide(false)}
      />
    </div>
  );
};

// Step 3: Asset Details section
const AssetDetailsStep = ({
  onNext,
  onPrevious,
  company,
  isCreateFlow,
  programId,
  vaultConfigs,
  onVaultConfigChange
}: {
  onNext: () => void;
  onPrevious: () => void;
  company?: Company | null;
  isCreateFlow?: boolean;
  programId?: number;
  vaultConfigs?: VaultLinkConfig[];
  onVaultConfigChange?: (configs: VaultLinkConfig[]) => void;
}) => {
  const { setValue, getValues, watch, register } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Watch form values - these will update when draft is recovered
  const targets = watch('targets') || [];
  const attachments = watch('attachments') || [];
  const existingAttachmentLinks = watch('existingAttachments') || [];

  // Local state for UI
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);


  // Sync uploaded files with form attachments when draft is recovered
  useEffect(() => {
    if (attachments && Array.isArray(attachments)) {
      setUploadedFiles(attachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments.length]);

  // No local state for credentials; bind directly to RHF
  // Asset selection is now handled by ProgramAssetSelector component

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const currentFiles = getValues('attachments') || [];
      const allFiles = [...currentFiles, ...newFiles];
      setValue('attachments', allFiles);
      setUploadedFiles(allFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    const currentFiles = getValues('attachments') || [];
    const updatedFiles = currentFiles.filter((_: any, i: number) => i !== index);
    setValue('attachments', updatedFiles);
    setUploadedFiles(updatedFiles);
  };

  const handleRemoveExistingLink = (index: number) => {
    const currentLinks = getValues('existingAttachments') || [];
    const updatedLinks = currentLinks.filter((_: any, i: number) => i !== index);
    setValue('existingAttachments', updatedLinks);
  };

  const getFileNameFromUrl = (link: string): string => {
    try {
      const url = new URL(link);
      const last = url.pathname.split('/').filter(Boolean).pop() || link;
      return decodeURIComponent(last);
    } catch {
      const parts = link.split('/');
      return decodeURIComponent(parts[parts.length - 1] || link);
    }
  };

  const getFileExt = (name: string): string => {
    const parts = name.split('.');
    return parts.length > 1 ? parts.pop()!.toUpperCase() : '';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="w-full rounded-b-xl border border-t-0 border-gray-200 bg-white shadow-lg overflow-hidden mb-6">
      <div className="p-6 sm:p-8 lg:p-10">
        <h2 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Asset Details</h2>
        <p className="mb-6 text-sm sm:text-base text-slate-600">Specify the assets that will be included in the security program</p>

        <div className="space-y-6">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-slate-700">
              Targets
            </label>
            <ProgramTargetsEditor
              value={targets}
              onChange={(newTargets) => setValue('targets', newTargets, { shouldDirty: true })}
            />
            <p className="mt-1 text-xs text-slate-500">
              Add the targets and endpoints to be tested.
            </p>
          </div>

          {/* Upload Asset Documentation */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-slate-700">
              Upload Asset Documentation <span className="text-xs text-slate-500">(optional)</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 md:p-8 text-center hover:border-gray-400 transition-colors cursor-pointer min-h-[140px] md:min-h-[180px]"
              style={{ backgroundColor: '#F8FAFC' }}
            >
              <div className="flex flex-col items-center">
                <img
                  src="/icons/upload_icon.svg"
                  alt="Upload"
                  className="w-12 h-12 mb-3"
                />
                <p className="text-sm font-medium text-slate-700 mb-1">Click to upload asset documentation</p>
                <p className="text-xs text-slate-500">Supports any file type</p>
                <p className="text-xs text-slate-500">Max 10 MB files are allowed</p>
              </div>
            </div>
          </div>

          {/* Existing Attachments (from server) */}
          {Array.isArray(existingAttachmentLinks) && existingAttachmentLinks.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-slate-700">Existing Attachments</h4>
              <ul className="space-y-3">
                {existingAttachmentLinks.map((link: string, index: number) => {
                  const name = getFileNameFromUrl(link);
                  const ext = getFileExt(name);
                  return (
                    <li key={index} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                          {/* Paperclip icon */}
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-8.49 8.49a6 6 0 01-8.49-8.49l9.19-9.19a4.24 4.24 0 016 6l-9.19 9.19a2.48 2.48 0 11-3.51-3.51l8.49-8.49" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900" title={name}>{name}</p>
                          <p className="text-xs text-slate-500">{ext || 'FILE'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <a href={link} target="_blank" rel="noreferrer" download className="text-blue-600 hover:text-blue-700" title="Download">
                          {/* Download icon */}
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                            <path d="M7 10l5 5 5-5" />
                            <path d="M12 15V3" />
                          </svg>
                        </a>
                        <button type="button" onClick={() => handleRemoveExistingLink(index)} className="text-red-500 hover:text-red-600" title="Delete">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="flex flex-col">
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: '#010D2C' }}>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white">File Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white">Size</th>
                      <th className="px-4 py-3 w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {uploadedFiles.map((file, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-slate-900">{file.name}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{formatFileSize(file.size)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Secure Credential Vault */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-slate-700">
              Secure Credentials
            </label>
            {programId ? (
              <VaultSelector programId={programId} canEdit={true} />
            ) : (
              <VaultPreSelector
                selectedVaultIds={watch('selectedVaultIds') || []}
                onSelectionChange={(vaultIds) => setValue('selectedVaultIds', vaultIds)}
                vaultConfigs={vaultConfigs || []}
                onVaultConfigChange={onVaultConfigChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="px-6 sm:px-8 lg:px-10 py-4 border-t border-gray-200 flex items-center justify-between" style={{ backgroundColor: '#F8FAFC' }}>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          type="button"
          onClick={onPrevious}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>
        <button
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          onClick={onNext}
          type="button"
        >
          Next
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Step 4: Testing Details section
const TestingDetailsStep = ({
  onNext,
  onPrevious
}: {
  onNext: () => void;
  onPrevious: () => void;
}) => {
  const { setValue, getValues } = useFormContext();

  // Use local state for inputs to avoid re-render issues
  const [testingMethodology, setTestingMethodology] = useState('');
  const [environmentType, setEnvironmentType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [complianceType, setComplianceType] = useState('');
  const [otherCompliance, setOtherCompliance] = useState('');
  // Remove local state for other; use RHF directly

  // Load initial values from form on mount and when form data changes
  useEffect(() => {
    const loadFormValues = () => {
      setTestingMethodology(getValues('testingType') || '');
      setEnvironmentType(getValues('environmentType') || '');
      const startDateVal = getValues('expectedStartDate');
      const endDateVal = getValues('expectedEndDate');
      setStartDate(startDateVal ? (startDateVal instanceof Date ? startDateVal.toISOString().split('T')[0] : startDateVal) : '');
      setEndDate(endDateVal ? (endDateVal instanceof Date ? endDateVal.toISOString().split('T')[0] : endDateVal) : '');
      setComplianceType(getValues('complianceType') || '');
      setOtherCompliance(getValues('otherComplianceType') || '');
    };

    loadFormValues();

    // Reload values when window event is triggered (for draft recovery)
    const handleFormUpdate = () => loadFormValues();
    window.addEventListener('programFormUpdated', handleFormUpdate);

    return () => {
      window.removeEventListener('programFormUpdated', handleFormUpdate);
    };
  }, [getValues]);

  // No syncing effect needed

  const handleChange = (field: string, value: string | Date, localSetter: (v: any) => void) => {
    localSetter(value);
    setValue(field, value);
  };

  const currentType = getValues('type');

  return (
    <div className="w-full rounded-b-xl border border-t-0 border-gray-200 bg-white shadow-lg overflow-hidden mb-6">
      <div className="p-6 sm:p-8 lg:p-10">
        <h2 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Testing Details</h2>
        <p className="mb-6 text-sm sm:text-base text-slate-600">Define the testing methodology and requirements for your security program</p>

        <div className="space-y-6">
          {currentType === ProgramType.PTAAS && (
            <>
              <FormSelect
                name="testingType"
                label="Testing Methodology"
                options={[
                  { value: "Black Box", label: "Black Box" },
                  { value: "Grey Box", label: "Grey Box" },
                  { value: "White Box", label: "White Box" }
                ]}
                placeholder="Select Methodology"
                value={testingMethodology}
                onChange={(value) => handleChange('testingType', value, setTestingMethodology)}
                required={true}
              />

              <FormSelect
                name="environmentType"
                label="Environment Type"
                options={[
                  { value: "Production", label: "Production" },
                  { value: "Staging", label: "Staging" }
                ]}
                placeholder="Select Environment Type"
                value={environmentType}
                onChange={(value) => handleChange('environmentType', value, setEnvironmentType)}
                required={true}
              />

              {/* Testing Timeline */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-slate-700">
                  Testing Timeline <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormDateInput
                    name="expectedStartDate"
                    label="Start Date"
                    value={startDate}
                    onChange={(value) => { setStartDate(value); setValue('expectedStartDate', value); }}
                    placeholder="Start Date: MM/DD/YYYY"
                    required={true}
                  />
                  <FormDateInput
                    name="expectedEndDate"
                    label="End Date"
                    value={endDate}
                    onChange={(value) => { setEndDate(value); setValue('expectedEndDate', value); }}
                    placeholder="End Date: MM/DD/YYYY"
                    required={true}
                  />
                </div>
              </div>

              <FormSelect
                name="complianceType"
                label="Compliance Type"
                options={[
                  { value: "SOC2", label: "SOC2" },
                  { value: "HIPAA", label: "HIPAA" },
                  { value: "ISO27001", label: "ISO27001" },
                  { value: "PCIDSS", label: "PCIDSS" },
                  { value: "Other", label: "Other" }
                ]}
                placeholder="Select Compliance Type"
                value={complianceType}
                onChange={(value) => handleChange('complianceType', value, setComplianceType)}
                required={true}
              />

              {/* Other Compliance - Shows when "Other" is selected */}
              {complianceType === "Other" && (
                <FormInput
                  name="otherComplianceType"
                  label="Specify Compliance Type"
                  value={otherCompliance}
                  onChange={(value) => handleChange('otherComplianceType', value, setOtherCompliance)}
                  placeholder="Mention the type of compliance, mention NA if not applicable"
                />
              )}
            </>
          )}

          <FormTextarea
            name="other"
            label="Other"
            placeholder="Provide any essential information that has not been disclosed."
            rows={4}
            value={useWatch({ name: 'other' }) || ''}
            onChange={(value) => setValue('other', value)}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="px-6 sm:px-8 lg:px-10 py-4 border-t border-gray-200 flex items-center justify-between" style={{ backgroundColor: '#F8FAFC' }}>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          type="button"
          onClick={onPrevious}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>
        <button
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          onClick={onNext}
          type="button"
        >
          Next
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Step 5: Review & Submit section
const ReviewSubmitStep = ({
  onPrevious,
  skipCompany,
  slackSelected,
  jiraSelected
}: {
  onPrevious: () => void;
  skipCompany?: boolean;
  slackSelected?: boolean;
  jiraSelected?: boolean;
}) => {
  const { watch } = useFormContext();
  const [confirmChecked, setConfirmChecked] = useState(false);

  // Get actual form data
  const title = watch('title') || '';
  const type = watch('type') || '';
  const scope = watch('scope') || '';
  const description = watch('description') || '';
  const targets = watch('targets') || [];
  const testingType = watch('testingType') || '';
  const environmentType = watch('environmentType') || '';
  const expectedStartDate = watch('expectedStartDate');
  const expectedEndDate = watch('expectedEndDate');
  const companyData = watch('companyDetails') || {};
  const assetIds: number[] = watch('assetIds') || [];

  // Get integration configuration data
  const slackChannelLink = watch('slack_channel_link') || '';
  const jiraUrl = watch('jira_url') || '';
  const jiraEmail = watch('jira_email') || '';
  const jiraApiToken = watch('jira_api_token') || '';
  const jiraProjectKey = watch('jira_project_key') || '';

  // Validate Slack configuration
  const isSlackConfigValid = slackSelected ? (
    slackChannelLink &&
    slackChannelLink.trim() !== '' &&
    /^https:\/\/hooks\.slack\.com\/services\//.test(slackChannelLink.trim())
  ) : true;

  // Validate Jira configuration
  const isJiraConfigValid = jiraSelected ? (
    jiraUrl && jiraUrl.trim() !== '' &&
    jiraEmail && jiraEmail.trim() !== '' &&
    jiraApiToken && jiraApiToken.trim() !== '' &&
    jiraProjectKey && jiraProjectKey.trim() !== ''
  ) : true;

  // Type-specific fields (watched unconditionally)
  const outOfScope = watch('outOfScope') || '';
  const knownVulnerabilities = watch('knownVulnerabilities') || '';
  const termsOfService = watch('termsOfService') || '';
  const rewards = watch('rewards') || {} as any;
  const rewardPolicy = watch('rewardPolicy') || '';
  const privateVisibility = watch('private');

  // Build review data from actual form values
  const formatDate = (date: any) => {
    if (!date) return '';
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    return '';
  };

  const reviewData = {
    companyDetails: {
      companyName: { value: companyData?.companyName || "Not provided", provided: !!companyData?.companyName },
      industry: { value: companyData?.industry || "Not provided", provided: !!companyData?.industry },
      contactPerson: { value: companyData?.contactName || "Not provided", provided: !!companyData?.contactName },
      contactEmail: { value: companyData?.contactEmail || "Not provided", provided: !!companyData?.contactEmail },
      contactPhone: { value: companyData?.contactPhone || "Not provided", provided: !!companyData?.contactPhone },
      companySize: { value: companyData?.companySize || "Not provided", provided: !!companyData?.companySize },
      companyAddress: { value: companyData?.companyAddress || "Not provided", provided: !!companyData?.companyAddress }
    },
    programDetails: {
      programName: { value: title || "Not provided", provided: !!title },
      programType: { value: type || "Not provided", provided: !!type },
      programScope: { value: scope || "Not provided", provided: !!scope },
      description: { value: description || "Not provided", provided: !!description }
    },
    assetDetails: {
      assetList: {
        value: (() => {
          const assetCount = assetIds.length;
          const targetCount = targets.length;
          const parts = [];

          if (assetCount > 0) parts.push(`${assetCount} asset${assetCount === 1 ? '' : 's'}`);
          if (targetCount > 0) parts.push(`${targetCount} target${targetCount === 1 ? '' : 's'}`);

          return parts.length > 0 ? parts.join(' and ') + ' selected' : "Not provided";
        })(),
        provided: assetIds.length > 0 || targets.length > 0
      }
    },
    testingDetails: {
      testingMethodology: { value: testingType || "Not provided", provided: !!testingType },
      environmentType: { value: environmentType || "Not provided", provided: !!environmentType },
      startDate: { value: formatDate(expectedStartDate) || "Not provided", provided: !!expectedStartDate },
      endDate: { value: formatDate(expectedEndDate) || "Not provided", provided: !!expectedEndDate }
    }
  };

  const handleSubmit = () => {
    if (!confirmChecked) {
      alert("Please confirm that all information is accurate before submitting");
      return;
    }
    // Form submission logic will be handled by parent form
    console.log("Form submitted");
  };

  const renderField = (label: string, field: { value: string; provided: boolean }) => (
    <div className="py-4 border-b border-gray-200 last:border-0">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
          <p className="text-sm text-slate-900">{field.value}</p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {field.provided ? (
            <>
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium text-green-600">Provided</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium text-red-600">Missing</span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderSimple = (label: string, value: string, required = false) => (
    <div className="py-4 border-b border-gray-200 last:border-0">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
          </p>
          <p className="text-sm text-slate-900">{value || 'Not provided'}</p>
        </div>
        {required && (
          <div className="flex items-center gap-2 ml-4">
            {value ? (
              <span className="text-xs font-medium text-green-600">Provided</span>
            ) : (
              <span className="text-xs font-medium text-red-600">Missing</span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const formatRewards = (tiers: any) => {
    if (!tiers) return '';
    const parts: string[] = [];
    if (tiers.low !== undefined) parts.push(`Low: ${tiers.low}`);
    if (tiers.medium !== undefined) parts.push(`Medium: ${tiers.medium}`);
    if (tiers.high !== undefined) parts.push(`High: ${tiers.high}`);
    if (tiers.critical !== undefined) parts.push(`Critical: ${tiers.critical}`);
    return parts.join(' | ');
  };

  return (
    <div className="w-full rounded-b-xl border border-t-0 border-gray-200 bg-white shadow-lg overflow-hidden mb-6">
      <div className="p-6 sm:p-8 lg:p-10">
        <h2 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Review & Submit</h2>
        <p className="mb-6 text-sm sm:text-base text-slate-600">Please review all the information before submitting your security program</p>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-blue-700">
              Once submitted, your program will be reviewed by our security team. You'll receive a confirmation email with next steps.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Company Details Section (hidden when company step is skipped) */}
          {!skipCompany && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold text-slate-900">Company Details</h3>
              </div>
              <div className="px-6">
                {renderField("Company Name", reviewData.companyDetails.companyName)}
                {renderField("Industry", reviewData.companyDetails.industry)}
                {renderField("Contact Person", reviewData.companyDetails.contactPerson)}
                {renderField("Contact Email", reviewData.companyDetails.contactEmail)}
                {renderField("Contact Phone", reviewData.companyDetails.contactPhone)}
                {renderField("Company Size", reviewData.companyDetails.companySize)}
                {renderField("Company Address", reviewData.companyDetails.companyAddress)}
              </div>
            </div>
          )}

          {/* Program Details Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-slate-900">Program Details</h3>
            </div>
            <div className="px-6">
              {renderField("Program Name", reviewData.programDetails.programName)}
              {renderField("Program Type", reviewData.programDetails.programType)}
              {renderField("Program Scope", reviewData.programDetails.programScope)}
              {renderField("Description", reviewData.programDetails.description)}
              {/* Type-specific review rows */}
              {type === 'VDP' && (
                <>
                  {renderSimple('Out of Scope', outOfScope, true)}
                  {renderSimple('Known Vulnerabilities', knownVulnerabilities, false)}
                  {renderSimple('Terms and Conditions', termsOfService, true)}
                </>
              )}
              {type === 'Bug Bounty' && (
                <>
                  {renderSimple('Program Access', privateVisibility === 1 ? 'Private' : 'Public', true)}
                  {renderSimple('Reward Tiers', formatRewards(rewards), true)}
                  {renderSimple('Reward Policy', rewardPolicy, true)}
                  {renderSimple('Terms and Conditions', termsOfService, true)}
                </>
              )}
            </div>
          </div>

          {/* Asset Details Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-slate-900">Asset Details</h3>
            </div>
            <div className="px-6">
              {renderField("Asset List", reviewData.assetDetails.assetList)}
            </div>
          </div>

          {/* Testing Details Section (PTaaS only) */}
          {type === 'PTAAS' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold text-slate-900">Testing Details</h3>
              </div>
              <div className="px-6">
                {renderField("Testing Methodology", reviewData.testingDetails.testingMethodology)}
                {renderField("Environment Type", reviewData.testingDetails.environmentType)}
                {renderField("Start Date", reviewData.testingDetails.startDate)}
                {renderField("End Date", reviewData.testingDetails.endDate)}
              </div>
            </div>
          )}

          {/* Integrations Section - Only show if any integration is selected */}
          {(slackSelected || jiraSelected) && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold text-slate-900">Integrations</h3>
              </div>
              <div className="px-6">
                {/* Slack Integration Status */}
                {slackSelected && (
                  <div className="py-4 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex h-6 w-6 items-center justify-center rounded bg-[#4A154B]">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-slate-700">Slack Integration</p>
                        </div>
                        <p className="text-sm text-slate-600 ml-8">
                          {slackChannelLink ? (
                            isSlackConfigValid ? 'Webhook URL configured' : 'Invalid webhook URL format'
                          ) : 'Webhook URL not provided'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {isSlackConfigValid && slackChannelLink ? (
                          <>
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-green-600">Configured</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-red-600">
                              {slackChannelLink ? 'Invalid' : 'Missing'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Jira Integration Status */}
                {jiraSelected && (
                  <div className="py-4 border-b border-gray-200 last:border-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex h-6 w-6 items-center justify-center rounded bg-[#0052CC]">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0z" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-slate-700">Jira Integration</p>
                        </div>
                        <div className="ml-8 space-y-1">
                          <p className="text-sm text-slate-600">
                            Site URL: {jiraUrl || <span className="text-red-500">Not provided</span>}
                          </p>
                          <p className="text-sm text-slate-600">
                            Email: {jiraEmail || <span className="text-red-500">Not provided</span>}
                          </p>
                          <p className="text-sm text-slate-600">
                            API Token: {jiraApiToken ? '••••••••' : <span className="text-red-500">Not provided</span>}
                          </p>
                          <p className="text-sm text-slate-600">
                            Project Key: {jiraProjectKey || <span className="text-red-500">Not provided</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {isJiraConfigValid ? (
                          <>
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-green-600">Configured</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-red-600">Incomplete</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation Checkbox */}
          <div className="mt-8">
            <div className="rounded-xl px-4 py-4" style={{ backgroundColor: '#F8FAFC' }}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm leading-6 text-slate-700">
                  I confirm that all the information provided is accurate and complete. I understand that our security program will be set up based on this information.
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="px-6 sm:px-8 lg:px-10 py-4 border-t border-gray-200 flex items-center justify-between" style={{ backgroundColor: '#F8FAFC' }}>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          type="button"
          onClick={onPrevious}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>
        <div className="flex items-center gap-3">
          {/* Show warning if integrations are incomplete */}
          {(slackSelected && !isSlackConfigValid) || (jiraSelected && !isJiraConfigValid) ? (
            <div className="flex items-center gap-2 text-amber-600 text-sm">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>Integration config incomplete</span>
            </div>
          ) : null}
          <button
            className={`flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${confirmChecked && isSlackConfigValid && isJiraConfigValid
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
              }`}
            onClick={handleSubmit}
            disabled={!confirmChecked || !isSlackConfigValid || !isJiraConfigValid}
            type="submit"
          >
            {useParams().id ? 'Save Changes' : 'Submit Program'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Lightweight "Step 1: Company Details" section (frontend-only for now)
const CompanyDetailsStep = ({
  company,
  onNext,
  isAdminRole
}: {
  company?: Company | null;
  onNext: () => void;
  isAdminRole: boolean;
}) => {
  const { watch, setValue } = useFormContext();
  const companyDetails = watch('companyDetails') || {
    companyName: company?.company_name || "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    industry: "",
    companySize: "",
    companyAddress: ""
  };

  // When the fetched company prop changes, update the form's company name
  useEffect(() => {
    if (company?.company_name) {
      setValue('companyDetails.companyName', company.company_name);
    }
  }, [company, setValue]);

  const [errors, setErrors] = useState({
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    industry: "",
    companySize: "",
    companyAddress: ""
  });

  const [touched, setTouched] = useState({
    companyName: false,
    contactName: false,
    contactEmail: false,
    contactPhone: false,
    industry: false,
    companySize: false,
    companyAddress: false
  });

  // Validation functions
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[\d\s\-+\(\)]+$/;
    return phone.length >= 10 && phoneRegex.test(phone);
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'companyName':
        return ''; // Not required for development
      case 'contactName':
        return ''; // Not required for development
      case 'contactEmail':
        return value.trim() !== '' && !validateEmail(value) ? 'Invalid email address' : '';
      case 'contactPhone':
        return value.trim() !== '' && !validatePhone(value) ? 'Valid phone number is required' : '';
      case 'industry':
        return ''; // Not required for development
      case 'companySize':
        return ''; // Not required for development
      case 'companyAddress':
        return ''; // Not required for development
      default:
        return '';
    }
  };

  const handleBlur = (field: keyof typeof touched) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, companyDetails[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (field: string, value: string) => {
    setValue(`companyDetails.${field}`, value);
    if (touched[field as keyof typeof touched]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleNext = async () => {
    // Validate all fields
    const newErrors = {
      companyName: validateField('companyName', companyDetails.companyName),
      contactName: validateField('contactName', companyDetails.contactName),
      contactEmail: validateField('contactEmail', companyDetails.contactEmail),
      contactPhone: validateField('contactPhone', companyDetails.contactPhone),
      industry: validateField('industry', companyDetails.industry),
      companySize: validateField('companySize', companyDetails.companySize),
      companyAddress: validateField('companyAddress', companyDetails.companyAddress)
    };

    setErrors(newErrors);
    setTouched({
      companyName: true,
      contactName: true,
      contactEmail: true,
      contactPhone: true,
      industry: true,
      companySize: true,
      companyAddress: true
    });

    const hasErrors = Object.values(newErrors).some(error => error !== '');
    if (!hasErrors) {
      // Data is already saved to form context, just proceed
      onNext();
    }
  };

  return (
    <div className="w-full rounded-b-xl border border-t-0 border-gray-200 bg-white shadow-lg overflow-hidden mb-6">
      <div className="p-6 sm:p-8 lg:p-10">
        <h2 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Company Information</h2>
        <p className="mb-6 text-sm sm:text-base text-slate-600">Please provide details about your organization</p>

        <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
          <FormInput
            name="companyName"
            label="Company Name"
            value={companyDetails.companyName}
            onChange={(value) => handleChange('companyName', value)}
            onBlur={() => handleBlur('companyName')}
            placeholder="Company Name"
            disabled
            description="This is automatically filled from your account"
            error={touched.companyName ? errors.companyName : undefined}
            required={true}
          />

          <FormSelect
            name="industry"
            label="Industry/Sector"
            options={[
              { value: "technology", label: "Technology" },
              { value: "finance", label: "Finance" },
              { value: "healthcare", label: "Healthcare" },
              { value: "retail", label: "Retail" },
              { value: "manufacturing", label: "Manufacturing" },
              { value: "education", label: "Education" },
              { value: "automotive_transportation", label: "Automotive & Transportation" },
              { value: "banking_financial_services", label: "Banking & Financial Services" },
              { value: "ecommerce_retail", label: "E-commerce & Retail" },
              { value: "education_edtech", label: "Education & EdTech" },
              { value: "ai_security_infrastructure", label: "AI Security & Infrastructure" },
              { value: "health_safety_tech", label: "Health & Safety Tech" },
              { value: "legal_compliance", label: "Legal & Compliance" },
              { value: "saas_cloud_platforms", label: "SaaS & Cloud Platforms" },
              { value: "telecom_media", label: "Telecom & Media" },
              { value: "other", label: "Other" }
            ]}
            placeholder="Select Industry"
            value={companyDetails.industry}
            onChange={(value) => handleChange('industry', value)}
            onBlur={() => handleBlur('industry')}
            error={touched.industry ? errors.industry : undefined}
            required={true}
          />

          <FormInput
            name="contactName"
            label="Contact Person Name"
            value={companyDetails.contactName}
            onChange={(value) => handleChange('contactName', value)}
            onBlur={() => handleBlur('contactName')}
            placeholder="e.g., Jane Doe"
            error={touched.contactName ? errors.contactName : undefined}
            required={true}
          />

          <FormInput
            name="contactEmail"
            label="Contact Email"
            type="email"
            value={companyDetails.contactEmail}
            onChange={(value) => handleChange('contactEmail', value)}
            onBlur={() => handleBlur('contactEmail')}
            placeholder="john.doe@example.com"
            error={touched.contactEmail ? errors.contactEmail : undefined}
            required={true}
          />

          <FormInput
            name="contactPhone"
            label="Contact Phone"
            type="tel"
            value={companyDetails.contactPhone}
            onChange={(value) => handleChange('contactPhone', value)}
            onBlur={() => handleBlur('contactPhone')}
            placeholder="+1 555 0123"
            error={touched.contactPhone ? errors.contactPhone : undefined}
            required={true}
          />

          <FormSelect
            name="companySize"
            label="Company Size"
            options={[
              { value: "1-10", label: "1-10 employees" },
              { value: "11-50", label: "11-50 employees" },
              { value: "51-200", label: "51-200 employees" },
              { value: "201-500", label: "201-500 employees" },
              { value: "501-1000", label: "501-1000 employees" },
              { value: "1001+", label: "1001+ employees" }
            ]}
            placeholder="Select Company Size"
            value={companyDetails.companySize}
            onChange={(value) => handleChange('companySize', value)}
            onBlur={() => handleBlur('companySize')}
            error={touched.companySize ? errors.companySize : undefined}
            required={true}
          />

          <FormTextarea
            name="companyAddress"
            label="Company Address"
            value={companyDetails.companyAddress}
            onChange={(value) => handleChange('companyAddress', value)}
            onBlur={() => handleBlur('companyAddress')}
            placeholder="123 Security Street, Cyber City, CC 12345"
            rows={3}
            className="lg:col-span-2"
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
            error={touched.companyAddress ? errors.companyAddress : undefined}
            required={true}
          />
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="px-6 sm:px-8 lg:px-10 py-4 border-t border-gray-200 flex items-center justify-between" style={{ backgroundColor: '#F8FAFC' }}>
        <button
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
          type="button"
          disabled
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>
        <button
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          onClick={handleNext}
          type="button"
        >
          Next
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Enhanced component for form-dependent functionality with auto-save
const ProgramEditorFormWrapper = () => {
  const { id } = useParams();
  const formContext = useFormContext();
  const lastSavedDataRef = useRef<string>('');
  // Legacy autosave UX: only show 'saved' when the debounced write completes

  // Auto-save functionality with 5-second intervals
  useEffect(() => {
    if (!formContext) return;

    const { getValues, watch } = formContext;

    // Function to save data
    const saveData = (data: any, isManual = false) => {
      try {
        // Prepare data for saving (handle Files and Dates)
        const { prepared: preparedData, warnings } = prepareDataForSaving(data);
        const serializedData = JSON.stringify(preparedData);
        const dataSize = new Blob([serializedData]).size;

        // Performance and size validation
        if (dataSize > MAX_FORM_DATA_SIZE) {
          const errorMsg = `Form data too large (${Math.round(dataSize / 1024 / 1024)}MB). Maximum ${Math.round(MAX_FORM_DATA_SIZE / 1024 / 1024)}MB allowed.`;
          if (isManual) {
            toast.error(errorMsg);
          }
          console.warn(errorMsg);
          return;
        }

        // Performance throttling for large data
        if (dataSize > PERFORMANCE_THROTTLE_SIZE && !isManual) {
          // Skip auto-save for large data to prevent performance issues
          console.log(`Skipping auto-save due to large data size: ${Math.round(dataSize / 1024)}KB`);
          return;
        }

        // Show warnings if any files were too large
        if (warnings.length > 0 && isManual) {
          warnings.forEach(warning => {
            toast.error(warning);
          });
        }

        // Only save if data has changed
        if (serializedData === lastSavedDataRef.current && !isManual) {
          console.log('No changes detected, skipping save');
          return;
        }

        console.log('Data changes detected, proceeding with save. Data size:', Math.round(dataSize / 1024), 'KB');

        const saveData = {
          data: preparedData,
          timestamp: Date.now(),
          version: '1.0',
          isManual,
          warnings: warnings.length > 0 ? warnings : undefined,
          dataSize,
          performance: {
            isLarge: dataSize > PERFORMANCE_THROTTLE_SIZE,
            sizeKB: Math.round(dataSize / 1024)
          }
        };

        // Use global keys for both manual and auto saves to persist across reloads
        // Only use tab-specific keys for conflict resolution in multi-tab scenarios
        const baseKey = id || 'new';
        const storageKey = isManual ? `program-draft-${baseKey}` : `program-autosave-${baseKey}`;

        const success = storageManager.setItem(storageKey, JSON.stringify(saveData));

        if (!success && isManual) {
          toast('Draft saved to temporary storage. Data may be lost on page refresh.', {
            icon: '⚠️',
            duration: 4000
          });
        } else if (!success) {
          console.warn('Auto-save failed, using fallback storage');
        }

        lastSavedDataRef.current = serializedData;

        // Update global status: show only 'saved' when write completes
        if ((window as any).updateAutoSaveStatus) {
          (window as any).updateAutoSaveStatus({
            status: 'saved',
            lastSaved: new Date(),
            isManual
          });
        }

        if (isManual) {
          toast.success('Draft saved successfully');
        }

        console.log(`${isManual ? 'Manual' : 'Auto'} save completed at`, new Date().toLocaleTimeString());

        // Show visual feedback for auto-saves (brief notification)
        if (!isManual) {
          // Create a brief visual indicator for auto-save
          const indicator = document.createElement('div');
          indicator.textContent = '✓ Auto-saved';
          indicator.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10B981;
            color: white;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 9999;
            opacity: 0;
            transition: opacity 0.3s ease;
          `;
          document.body.appendChild(indicator);

          // Animate in
          setTimeout(() => indicator.style.opacity = '1', 10);

          // Remove after 2 seconds
          setTimeout(() => {
            indicator.style.opacity = '0';
            setTimeout(() => document.body.removeChild(indicator), 300);
          }, 2000);
        }
      } catch (error) {
        console.error('Save error:', error);
        if (isManual) {
          toast.error('Failed to save draft');
        }

        if ((window as any).updateAutoSaveStatus) {
          (window as any).updateAutoSaveStatus({
            status: 'error',
            error: 'Failed to save draft'
          });
        }
      }
    };

    // Manual save function
    (window as any).programEditorManualSave = () => {
      const currentData = getValues();
      saveData(currentData, true);
    };

    // Expose getValues function for title updates
    (window as any).programEditorGetValues = getValues;

    // Recovery function with proper data restoration
    (window as any).programEditorRecoverDraft = (draftData: any) => {
      try {
        const { reset } = formContext;

        // Restore the draft data (handle Files and Dates)
        const restoredData = restoreDataFromSaved(draftData.data);

        reset(restoredData);

        // Trigger event to update step components
        window.dispatchEvent(new Event('programFormUpdated'));

        toast.success('Draft recovered successfully');
      } catch (error) {
        console.error('Error recovering draft:', error);
        toast.error('Failed to recover draft');
      }
    };

    // Auto-save on form changes (debounced)
    let debounceTimeout: ReturnType<typeof setTimeout>;
    const subscription = watch((data) => {
      console.log('Form data changed, scheduling debounced auto-save...');
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        console.log('Executing debounced auto-save');
        saveData(data, false);
      }, 2000); // keep responsive debounce
    });

    // Legacy: also run an interval auto-save every 5 seconds regardless of typing
    const intervalId = setInterval(() => {
      try {
        const currentData = getValues();
        saveData(currentData, false);
      } catch (e) {
        console.warn('Interval auto-save failed:', e);
      }
    }, 5000);

    console.log('Auto-save setup completed for program:', id || 'new');

    // Debug function to check localStorage
    (window as any).debugDrafts = () => {
      const baseKey = id || 'new';

      console.log('=== DRAFT DEBUG INFO ===');
      console.log('Base key:', baseKey);

      const autoSave = storageManager.getItem(`program-autosave-${baseKey}`);
      const draft = storageManager.getItem(`program-draft-${baseKey}`);
      const recovery = storageManager.getItem(`program-recovery-${baseKey}`);

      console.log('Auto-save:', autoSave ? 'EXISTS' : 'NOT FOUND');
      console.log('Draft:', draft ? 'EXISTS' : 'NOT FOUND');
      console.log('Recovery:', recovery ? 'EXISTS' : 'NOT FOUND');

      if (autoSave) {
        const parsed = JSON.parse(autoSave);
        console.log('Auto-save data:', parsed);
        console.log('Auto-save timestamp:', new Date(parsed.timestamp).toLocaleString());
      }
      if (draft) {
        const parsed = JSON.parse(draft);
        console.log('Draft data:', parsed);
        console.log('Draft timestamp:', new Date(parsed.timestamp).toLocaleString());
      }

      console.log('=== END DEBUG INFO ===');
    };

    // Debug function to check current form data
    (window as any).debugFormData = () => {
      const currentData = getValues();
      console.log('=== CURRENT FORM DATA ===');
      console.log('Form data:', currentData);
      console.log('Data keys:', Object.keys(currentData));
      console.log('Has meaningful data:', Object.values(currentData).some(value => {
        if (typeof value === 'string') return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
        return value !== null && value !== undefined;
      }));
      console.log('=== END FORM DATA ===');
    };

    // Save draft when user navigates away or closes the page
    const handleBeforeUnload = () => {
      const currentData = getValues();

      // Check if there's any data to save
      if (currentData && Object.keys(currentData).length > 0) {
        // Check if there's meaningful data (not just empty form)
        const hasData = Object.values(currentData).some(value => {
          if (typeof value === 'string') return value.trim().length > 0;
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
          return value !== null && value !== undefined;
        });

        if (hasData) {
          // Save immediately (synchronous)
          try {
            const { prepared: preparedData } = prepareDataForSaving(currentData);
            const saveData = {
              data: preparedData,
              timestamp: Date.now(),
              version: '1.0',
              isManual: false,
              isBeforeUnload: true
            };

            const baseKey = id || 'new';
            storageManager.setItem(`program-autosave-${baseKey}`, JSON.stringify(saveData));
            console.log('Draft saved before page unload');
          } catch (error) {
            console.error('Failed to save draft before unload:', error);
          }
        }
      }
    };

    // Add event listeners for page navigation
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Also save on visibility change (when user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const currentData = getValues();
        saveData(currentData, false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      clearTimeout(debounceTimeout);
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [formContext, id]);

  return null; // This component doesn't render anything
};

// Component for status bar and modals (outside EditorLayout)
const ProgramEditorStatusWrapper = () => {
  const { id } = useParams();
  const [autoSaveStatus, setAutoSaveStatus] = useState<{
    status: 'idle' | 'saving' | 'saved' | 'error';
    lastSaved?: Date;
    error?: string;
    isManual?: boolean;
  }>({ status: 'idle' });
  const [hasDraft, setHasDraft] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [currentTitle, setCurrentTitle] = useState('');

  // Expose status update function globally
  useEffect(() => {
    (window as any).updateAutoSaveStatus = (status: any) => {
      setAutoSaveStatus(status);
    };

    // Function to get current program title
    (window as any).getCurrentProgramTitle = () => {
      if ((window as any).programEditorGetValues) {
        try {
          const values = (window as any).programEditorGetValues();
          return values?.title || '';
        } catch (error) {
          return '';
        }
      }
      return '';
    };

    // Update title periodically
    const titleUpdateInterval = setInterval(() => {
      const newTitle = (window as any).getCurrentProgramTitle?.() || '';
      setCurrentTitle(newTitle);
    }, 1000); // Update every second

    return () => clearInterval(titleUpdateInterval);
  }, []);

  // Check for existing drafts on mount
  useEffect(() => {
    const checkDrafts = () => {
      try {
        const baseKey = id || 'new';

        // Check for drafts using global keys (both auto-save and manual save use global keys now)
        const autoSaveData = storageManager.getItem(`program-autosave-${baseKey}`);
        const draftDataStored = storageManager.getItem(`program-draft-${baseKey}`);
        const recoveryData = storageManager.getItem(`program-recovery-${baseKey}`);

        if (autoSaveData || draftDataStored || recoveryData) {
          console.log('Draft detected on page load!');
          console.log('Auto-save data:', !!autoSaveData);
          console.log('Draft data:', !!draftDataStored);
          console.log('Recovery data:', !!recoveryData);

          let data;
          try {
            data = autoSaveData ? JSON.parse(autoSaveData) :
              draftDataStored ? JSON.parse(draftDataStored) :
                JSON.parse(recoveryData!);

            // Parsed draft data loaded (restoration will occur during recovery)
            console.log('Draft data parsed successfully, showing modal');
            setHasDraft(true);
            setDraftData(data);
            setShowDraftModal(true);
          } catch (parseError) {
            console.error('Error parsing draft data:', parseError);
            // Clear corrupted data
            if (autoSaveData) storageManager.removeItem(`program-autosave-${baseKey}`);
            if (draftDataStored) storageManager.removeItem(`program-draft-${baseKey}`);
            if (recoveryData) storageManager.removeItem(`program-recovery-${baseKey}`);
            toast.error('Found corrupted draft data - cleared automatically');
          }
        } else {
          console.log('No drafts found for program:', baseKey);
        }
      } catch (error) {
        console.error('Error checking for drafts:', error);
      }
    };

    checkDrafts();
  }, [id]);

  const handleDiscardDraft = () => {
    try {
      const baseKey = id || 'new';

      // Clear all draft data
      storageManager.removeItem(`program-autosave-${baseKey}`);
      storageManager.removeItem(`program-draft-${baseKey}`);
      storageManager.removeItem(`program-recovery-${baseKey}`);
      setHasDraft(false);
      setDraftData(null);
      setShowDraftModal(false);
      toast.success('Draft discarded');
    } catch (error) {
      console.error('Error discarding draft:', error);
    }
  };

  const handleRecoverDraft = () => {
    if (!draftData) {
      toast.error('No draft data available');
      return;
    }

    try {
      // Use the global function; restoration happens inside the form wrapper
      if ((window as any).programEditorRecoverDraft) {
        (window as any).programEditorRecoverDraft(draftData);
        setShowDraftModal(false);
      } else {
        toast.error('Cannot recover draft - form not ready');
      }
    } catch (error) {
      console.error('Error recovering draft:', error);
      toast.error('Failed to recover draft');
    }
  };

  // Manual save functionality
  const handleManualSave = () => {
    try {
      setAutoSaveStatus({ status: 'saving' });

      // Use the global function
      if ((window as any).programEditorManualSave) {
        (window as any).programEditorManualSave();
        // Status will be updated by updateAutoSaveStatus from the form wrapper
      } else {
        setAutoSaveStatus({ status: 'error', error: 'Form not ready' });
        toast.error('Cannot save - form not ready');
      }
    } catch (error) {
      console.error('Manual save error:', error);
      setAutoSaveStatus({ status: 'error', error: 'Failed to save draft' });
      toast.error('Failed to save draft');
    }
  };

  const getDraftAge = () => {
    if (!draftData?.timestamp) return '';
    const now = Date.now();
    const diff = now - draftData.timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'just now';
  };

  const getDraftType = () => {
    if (!draftData) return '';
    if (draftData.recoveryTimestamp) return 'Error Recovery Data';
    if (draftData.error) return 'Failed Save Draft';
    return 'Auto-saved Draft';
  };

  return (
    <>
      {/* Enhanced top bar with better UX */}
      <EditorTopBar
        autoSaveStatus={autoSaveStatus}
        hasDraft={hasDraft}
        onManualSave={handleManualSave}
        onShowDraftRecovery={() => setShowDraftModal(true)}
        programTitle={currentTitle || draftData?.data?.title || ''}
        isEditing={!!id}
      />

      {/* Draft Recovery Modal */}
      {showDraftModal && draftData && (
        <DraftRecoveryModal
          state={{
            hasDraft,
            draftData,
            showPrompt: showDraftModal,
            isRecovering: false
          }}
          onRecover={handleRecoverDraft}
          onDiscard={handleDiscardDraft}
          onHide={() => setShowDraftModal(false)}
          getDraftAge={getDraftAge}
          getDraftType={getDraftType}
        />
      )}
    </>
  );
};

// Enhanced ProgramEditor component with auto-save and error handling
const ProgramEditorContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPogramType, setSelectedPogramType] = useState<ProgramType>(
    ProgramType.PTAAS
  );
  const { role } = useUserCredentials();
  const isAdminRole = role
    ? [
      UserRole.ADMIN,
      UserRole.ADMIN_MANAGER,
      UserRole.SUB_ADMIN,
      UserRole.QA
    ].includes(role)
    : false;

  // Check if user is a business role (BUSINESS, BUSINESS_MANAGER, BUSINESS_ADMINISTRATOR)
  const isBusinessRole = role
    ? [
      UserRole.BUSINESS,
      UserRole.BUSINESS_MANAGER,
      UserRole.BUSINESS_ADMINISTRATOR
    ].includes(role)
    : false;
  const { data: usersData } = useGetAllUsersQuery(
    isAdminRole
      ? { page: 0, limit: 50, roles: [UserRole.BUSINESS] }
      : (undefined as any),
    { skip: !isAdminRole }
  );
  const [privateVisibility, setPrivateVisibility] = useState<0 | 1 | undefined>(
    undefined
  ); // 0 = Public, 1 = Private
  // Always include CTB as a notification method
  const [notificationMethods, setNotificationMethods] = useState<String[]>([
    NotificationMethods.CTB
  ]);

  // Track which integrations are selected (Slack, Jira, or both)
  const [slackSelected, setSlackSelected] = useState<boolean>(false);
  const [jiraSelected, setJiraSelected] = useState<boolean>(false);

  // Vault configurations for new programs (stores access password settings before program creation)
  const [vaultConfigs, setVaultConfigs] = useState<VaultLinkConfig[]>([]);

  // Admin company select flow
  const [adminCompanies, setAdminCompanies] = useState<Array<{ company_id: number; company_name: string }>>([]);
  const [selectedCompanyNameAdmin, setSelectedCompanyNameAdmin] = useState<string>("");
  const [selectedOwnerIdAdmin, setSelectedOwnerIdAdmin] = useState<number | null>(null);
  useEffect(() => {
    if (isAdminRole) {
      listCompanyNamesAdmin().then(setAdminCompanies).catch(() => setAdminCompanies([]));
    }
  }, [isAdminRole]);

  // JIRA testing state
  const [jiraTestResult, setJiraTestResult] = useState<any>(null);

  // Jira setup guide modal state
  const [showJiraGuide, setShowJiraGuide] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);

  // Keep track of integration selections and notification methods
  const [selectedComplianceType, setSelectedComplianceType] = useState("");
  const { program, saveProgram } = useProgram(
    id !== undefined ? parseInt(id) : undefined
  );

  usePageTitle(id ? "Edit Program" : "Program Creation Form");

  // Test JIRA connection function
  const testJiraConnection = async (formData: any) => {
    setTesting(true);
    setJiraTestResult(null);

    try {
      const config = {
        url: formData.jira_url,
        email: formData.jira_email,
        api_token: formData.jira_api_token,
        project_key: formData.jira_project_key
      };

      // Validate all fields are provided
      if (
        !config.url ||
        !config.email ||
        !config.api_token ||
        !config.project_key
      ) {
        setJiraTestResult({
          success: false,
          error: "Please fill in all JIRA configuration fields before testing"
        });
        return;
      }

      console.log("Testing JIRA connection with config:", {
        ...config,
        api_token: config.api_token ? "[HIDDEN]" : "MISSING"
      });

      // Import axios instance
      const axios = (await import("../../utils/api/axios")).default;

      console.log("Making request to:", "/v2/programs/jira/validate");
      console.log("Backend base URL:", process.env.REACT_APP_BACKEND_BASE_URL);

      const response = await axios.post("/v2/programs/jira/validate", config);
      console.log("JIRA test response:", response.data);
      setJiraTestResult(response.data);
    } catch (error: any) {
      console.error("JIRA test error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });

      let errorMessage = "Failed to connect to server";
      let suggestions = ["Please check your internet connection and try again"];

      if (error.response?.status === 401) {
        errorMessage = "Authentication failed - Please log in again";
        suggestions = ["Try refreshing the page and logging in again"];
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied - Insufficient permissions";
        suggestions = ["Contact your administrator for access to JIRA testing"];
      } else if (error.response?.status === 404) {
        errorMessage = "API endpoint not found";
        suggestions = [
          "Please check that the backend server is running",
          "Verify the API endpoint is properly configured"
        ];
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        suggestions = error.response.data.suggestions || suggestions;
      }

      setJiraTestResult({
        success: false,
        error: errorMessage,
        suggestions: suggestions
      });
    } finally {
      setTesting(false);
    }
  };

  // Only showing the relevant part where we define defaultValues

  const defaultValues = useMemo(
    () =>
      ({
        title: program?.title || "",
        type: program?.type || (searchParams.get('type') || ProgramType.PTAAS),
        private:
          program?.private === undefined ? undefined : program.private ? 1 : 0,
        description: program?.description || "",
        targets: program?.targets || [],
        vpn: program?.vpn,
        credentials: program?.credentials,
        scope: program?.scope || "",
        outOfScope: program?.outOfScope || "",
        knownVulnerabilities: program?.knownVulnerabilities,
        rewards: program?.payoutRange || {},
        rewardPolicy: program?.rewardPolicy || "",
        termsOfService: program?.termsOfService || "",
        other: program?.other,
        profilePicture: undefined,
        testingType: program?.testing_type || "",
        environmentType: program?.environment_type || "",
        complianceType: program?.compliance_type || "",
        otherComplianceType: program?.other_compliance_type || "",
        expectedStartDate: program?.expected_start_date
          ? new Date(program?.expected_start_date)
          : undefined,
        expectedEndDate: program?.expected_end_date
          ? new Date(program?.expected_end_date)
          : undefined,
        notification_methods: program?.notification_methods,
        attachments: [],
        existingAttachments: program?.attachments || [],
        // Asset inventory selection (asset IDs). For now, we start with an empty list.
        assetIds: [],
        // Integration fields
        slack_channel_link: program?.slack_channel_link || "",
        jira_url: program?.jira_url || "",
        jira_email: program?.jira_email || "",
        jira_api_token: program?.jira_api_token || "",
        jira_project_key: program?.jira_project_key || "",
        // Vault selection for new programs
        selectedVaultIds: [],
        // Company details for draft persistence
        companyDetails: {
          companyName: "",
          contactName: "",
          contactEmail: "",
          contactPhone: "",
          industry: "",
          companySize: "",
          companyAddress: ""
        }
      }) as Omit<ProgramUpdateValues, "private"> & {
        private?: 0 | 1;
        existingAttachments?: string[];
        companyDetails?: any;
        selectedVaultIds?: number[];
      },
    [program, searchParams]
  );

  useEffect(() => {
    if (program?.type) setSelectedPogramType(program.type);
    if (program?.compliance_type)
      setSelectedComplianceType(program?.compliance_type);
  }, [program]);

  // Sync type from URL params to state for new programs
  useEffect(() => {
    if (!id && searchParams.get('type')) {
      const typeFromUrl = searchParams.get('type');
      if (typeFromUrl) {
        setSelectedPogramType(typeFromUrl as ProgramType);
      }
    }
  }, [id, searchParams]);

  useEffect(() => {
    if (selectedPogramType === ProgramType.VDP) {
      setPrivateVisibility(0);
    } else if (selectedPogramType === ProgramType.PTAAS) {
      setPrivateVisibility(1);
    } else {
      setPrivateVisibility(undefined);
    }
  }, [selectedPogramType]);

  // Set integration states based on existing program data
  useEffect(() => {
    if (program) {
      console.log("Program data loaded:", {
        slack_channel_link: program.slack_channel_link,
        jira_url: program.jira_url,
        jira_email: program.jira_email,
        jira_api_token: program.jira_api_token,
        jira_project_key: program.jira_project_key
      });

      // Check if Slack is configured
      const hasSlack = Boolean(program.slack_channel_link && program.slack_channel_link.trim() !== "");
      console.log("Slack detection:", { hasSlack, slack_channel_link: program.slack_channel_link });
      setSlackSelected(hasSlack);

      // Check if Jira is configured (all fields must be present)
      const hasJira = Boolean(
        program.jira_url && program.jira_url.trim() !== "" &&
        program.jira_email && program.jira_email.trim() !== "" &&
        program.jira_api_token && program.jira_api_token.trim() !== "" &&
        program.jira_project_key && program.jira_project_key.trim() !== ""
      );
      console.log("Jira detection:", {
        hasJira,
        jira_url: program.jira_url,
        jira_email: program.jira_email,
        jira_api_token: program.jira_api_token ? "[HIDDEN]" : null,
        jira_project_key: program.jira_project_key
      });
      setJiraSelected(hasJira);

      // Update notification methods based on existing integrations
      const methods = [NotificationMethods.CTB];
      if (hasSlack) methods.push(NotificationMethods.SLACK);
      if (hasJira) methods.push(NotificationMethods.JIRA);
      setNotificationMethods(methods);

      console.log("Final integration states:", { hasSlack, hasJira, methods });
    }
  }, [program]);

  // Frontend stepper state
  const [activeStep, setActiveStep] = useState<number>(0);
  const [company, setCompany] = useState<Company | null>(null);
  const [shouldSkipCompanyStep, setShouldSkipCompanyStep] = useState<boolean>(false);

  const initialCompanyId = useMemo(() => {
    const fromQuery = searchParams.get("companyId");
    return fromQuery ? parseInt(fromQuery, 10) : undefined;
  }, [searchParams]);

  useEffect(() => {
    // If editing an existing program, skip all admin/company steps but still fetch company
    if (id) {
      setShouldSkipCompanyStep(true);
      setActiveStep(0); // Start at Program Details
      // Still fetch company so we can use asset inventory when editing
      let cancelled = false;
      const fetchCompanyForEdit = async () => {
        try {
          const data = await getMyCompany();
          if (!cancelled && data) {
            setCompany(data);
          }
        } catch (error) {
          console.error('Failed to fetch company for edit mode:', error);
        }
      };
      fetchCompanyForEdit();
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    const fetchCompany = async () => {
      const data = initialCompanyId
        ? await getCompanyById(initialCompanyId)
        : await getMyCompany();
      if (!cancelled && data) {
        setCompany(data);
        const hasContact = (data as any)?.contact_person_name || (data as any)?.contactName;
        setShouldSkipCompanyStep(Boolean(hasContact));
        // If company details already exist, start at Program Details (mapped index 0)
        if (Boolean(hasContact)) setActiveStep(0);

        // For business users creating a new program, check if they have any assets with targets

      }
    };
    fetchCompany();
    return () => {
      cancelled = true;
    };
  }, [id, initialCompanyId, isBusinessRole, navigate]);

  return (
    <div className="bg-ctb-grey-250 overflow-x-hidden">
      {/* Inject our CSS animations */}
      <AnimationStyles />

      {/* Top: Title + autosave + actions inside the same grey container */}
      <div className="w-full px-4 pt-6">
        <ProgramEditorStatusWrapper />
      </div>

      {/* Stepper Header - Outside form for proper positioning */}
      {(activeStep >= 0 && activeStep <= 4) && (
        <div className="w-full px-4">
          <StepperHeader activeStep={activeStep} skipCompany={shouldSkipCompanyStep} isAdmin={isAdminRole && !id} />
        </div>
      )}

      <EditorLayout
        defaultValues={defaultValues}
        actions={[
          {
            label: id ? "Save" : "Create",
            type: "submit"
          }
        ]}
        hideFooter
        onSubmit={async (data: any) => {
          // Clone the data to avoid modifying the original
          const formData = { ...data };

          // Set private visibility and notification methods
          formData.private = privateVisibility;
          formData.notification_methods = notificationMethods;

          // Normalize privacy rules based on selected program type
          if (formData.type === 'VDP') {
            formData.private = 0; // VDP must be public
          }

          // Validate integration configurations

          // Validate Jira configuration if selected
          if (jiraSelected) {
            // Check all required Jira fields are filled
            const missingFields = [];

            if (!formData.jira_url) missingFields.push("Jira URL");
            if (!formData.jira_email) missingFields.push("Jira Email");
            if (!formData.jira_api_token) missingFields.push("Jira API Token");
            if (!formData.jira_project_key) missingFields.push("Jira Project Key");

            if (missingFields.length > 0) {
              toast.error(`Please fill in all required Jira fields: ${missingFields.join(", ")}`);
              return;
            }
          }

          // Validate Slack configuration if selected
          if (slackSelected && !formData.slack_channel_link) {
            toast.error("Please provide a Slack channel URL");
            return;
          }
          if (slackSelected && formData.slack_channel_link) {
            const slackWebhookPattern = /^https:\/\/hooks\.slack\.com\/services\//;
            if (!slackWebhookPattern.test(formData.slack_channel_link.trim())) {
              toast.error(
                <span className="break-all">
                  Please enter the Slack webhook in correct format: https://hooks.slack.com/services/XXXX/XXXX/XXXX
                </span>
              );
              return;
            }
          }

          // Normalize date fields back to Date objects (legacy behavior expected by backend)
          if (typeof formData.expectedStartDate === 'string' && formData.expectedStartDate) {
            formData.expectedStartDate = new Date(formData.expectedStartDate);
          }
          if (typeof formData.expectedEndDate === 'string' && formData.expectedEndDate) {
            formData.expectedEndDate = new Date(formData.expectedEndDate);
          }
          // Also handle snake_case keys defensively if downstream expects them directly
          if (typeof formData.expected_start_date === 'string' && formData.expected_start_date) {
            formData.expected_start_date = new Date(formData.expected_start_date);
          }
          if (typeof formData.expected_end_date === 'string' && formData.expected_end_date) {
            formData.expected_end_date = new Date(formData.expected_end_date);
          }

          // Attach admin overrides
          if (isAdminRole && !id) {
            if (selectedOwnerIdAdmin) {
              formData.owner_id = selectedOwnerIdAdmin;
            }
            if (company?.company_id) {
              formData.company_id = company.company_id;
            }
          }

          // STEP 1: Save company details if provided (before saving program)
          if (formData.companyDetails && !shouldSkipCompanyStep) {
            try {
              if (isAdminRole && company?.company_id) {
                await updateCompanyDetailsAdmin(company.company_id, {
                  industry: formData.companyDetails.industry || null,
                  contact_person_name: formData.companyDetails.contactName || null,
                  contact_email: formData.companyDetails.contactEmail || null,
                  contact_phone: formData.companyDetails.contactPhone || null,
                  company_size: formData.companyDetails.companySize || null,
                  company_address: formData.companyDetails.companyAddress || null
                });
              } else {
                await updateMyCompanyDetails({
                  industry: formData.companyDetails.industry || null,
                  contact_person_name: formData.companyDetails.contactName || null,
                  contact_email: formData.companyDetails.contactEmail || null,
                  contact_phone: formData.companyDetails.contactPhone || null,
                  company_size: formData.companyDetails.companySize || null,
                  company_address: formData.companyDetails.companyAddress || null
                });
              }
            } catch (error) {
              console.error('Failed to save company details:', error);
              toast.error('Failed to save company details. Please try again.');
              return;
            }
          }

          // STEP 2: Enhanced save with error handling
          // Extract vault configs before saving (they'll be linked after program creation)
          const vaultConfigsToLink = [...vaultConfigs];

          try {
            await new Promise<void>((resolve, reject) => {
              saveProgram(formData, {
                preserveFormState: true,
                onError: (error) => {
                  console.error('Save error:', error);
                  reject(error);
                },
                onSuccess: async (newProgramId) => {
                  // Link vaults with their configured access passwords
                  if (vaultConfigsToLink.length > 0 && !id && newProgramId) {
                    console.log(`Linking ${vaultConfigsToLink.length} vaults to program ${newProgramId}...`);
                    let linkedCount = 0;
                    let failedCount = 0;

                    for (const config of vaultConfigsToLink) {
                      try {
                        await linkVaultToProgram(config.vaultId, newProgramId, config.options);
                        linkedCount++;
                        console.log(`Linked vault ${config.vaultName} to program ${newProgramId}`);
                      } catch (error) {
                        console.error(`Failed to link vault ${config.vaultName}:`, error);
                        failedCount++;
                      }
                    }

                    if (failedCount === 0) {
                      toast.success(`Program created! ${linkedCount} credential${linkedCount !== 1 ? 's' : ''} linked successfully.`);
                    } else if (linkedCount > 0) {
                      toast.success(`Program created! ${linkedCount} credential${linkedCount !== 1 ? 's' : ''} linked. ${failedCount} failed - you can link them manually.`);
                    } else {
                      toast.error('Program created but failed to link credentials. Please link them manually in the Credentials section.');
                    }

                    // Clear vault configs after linking
                    setVaultConfigs([]);
                  }
                }
              });

              // Since saveProgram doesn't return a promise, we'll resolve immediately
              // The error handling is done via the onError callback
              resolve();
            });


          } catch (error) {
            // If save fails, the draft is already saved as fallback
            toast.error('Failed to save program. Your work has been saved as a draft.');
            console.error('Program save failed, but draft is preserved:', error);
          }
        }}
      >
        {/* Admin Step 0: Select Company */}
        {isAdminRole && !id && activeStep === 0 && (
          <div className="w-full px-4">
            <div className="w-full rounded-b-xl border border-t-0 border-gray-200 bg-white shadow-lg overflow-hidden mb-6">
              <div className="p-6 sm:p-8 lg:p-10">
                <h2 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Select Company</h2>
                <p className="mb-6 text-sm sm:text-base text-slate-600">Choose the company to create the program for</p>
                <div className="max-w-xl">
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    value={selectedCompanyNameAdmin}
                    onChange={async (e) => {
                      const name = e.target.value;
                      setSelectedCompanyNameAdmin(name);
                      if (!name) return;
                      const resolved = await resolveCompanyByNameAdmin(name);
                      if (resolved.company) {
                        setCompany(resolved.company as any);
                        const hasContact = (resolved.company as any)?.contact_person_name;
                        setShouldSkipCompanyStep(Boolean(hasContact));
                        setSelectedOwnerIdAdmin(resolved.owner?.user_id ?? null);
                        setActiveStep(1);
                      }
                    }}
                  >
                    <option value="">Select a Company</option>
                    {adminCompanies.map(c => (
                      <option key={c.company_id} value={c.company_name}>{c.company_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step Content - Inside Form Context with proper container */}
        {(!id && ((isAdminRole ? activeStep === 1 : activeStep === 0)) && !shouldSkipCompanyStep) && (
          <div className="w-full px-4">
            <CompanyDetailsStep
              company={company}
              onNext={() => setActiveStep(isAdminRole ? 2 : 1)}
              isAdminRole={isAdminRole}
            />
          </div>
        )}
        {(
          id
            ? activeStep === 0
            : shouldSkipCompanyStep
              ? (activeStep === (isAdminRole ? 1 : 0))
              : (activeStep === (isAdminRole ? 2 : 1))
        ) && (
            <div className="w-full px-4">
              <ProgramDetailsStep
                onNext={() => setActiveStep(id ? 1 : (shouldSkipCompanyStep ? (isAdminRole ? 2 : 1) : (isAdminRole ? 3 : 2)))}
                onPrevious={() => setActiveStep(id ? 0 : (shouldSkipCompanyStep ? (isAdminRole ? 0 : 0) : (isAdminRole ? 1 : 0)))}
                slackSelected={slackSelected}
                setSlackSelected={setSlackSelected}
                jiraSelected={jiraSelected}
                setJiraSelected={setJiraSelected}
                notificationMethods={notificationMethods}
                setNotificationMethods={setNotificationMethods}
              />
            </div>
          )}
        {(
          id
            ? activeStep === 1
            : shouldSkipCompanyStep
              ? (activeStep === (isAdminRole ? 2 : 1))
              : (activeStep === (isAdminRole ? 3 : 2))
        ) && (
            <div className="w-full px-4">
              <AssetDetailsStep
                onNext={() =>
                  setActiveStep(
                    id
                      ? 2
                      : shouldSkipCompanyStep
                        ? (isAdminRole ? 3 : 2)
                        : (isAdminRole ? 4 : 3)
                  )
                }
                onPrevious={() =>
                  setActiveStep(
                    id
                      ? 0
                      : shouldSkipCompanyStep
                        ? (isAdminRole ? 1 : 0)
                        : (isAdminRole ? 2 : 1)
                  )
                }
                company={company}
                isCreateFlow={!id}
                programId={id ? parseInt(id) : undefined}
                vaultConfigs={vaultConfigs}
                onVaultConfigChange={setVaultConfigs}
              />
            </div>
          )}
        {(
          id
            ? activeStep === 2
            : shouldSkipCompanyStep
              ? (activeStep === (isAdminRole ? 3 : 2))
              : (activeStep === (isAdminRole ? 4 : 3))
        ) && (
            <div className="w-full px-4">
              <TestingDetailsStep
                onNext={() => setActiveStep(id ? 3 : (shouldSkipCompanyStep ? (isAdminRole ? 4 : 3) : (isAdminRole ? 5 : 4)))}
                onPrevious={() => setActiveStep(id ? 1 : (shouldSkipCompanyStep ? (isAdminRole ? 2 : 1) : (isAdminRole ? 3 : 2)))}
              />
            </div>
          )}
        {(
          id
            ? activeStep === 3
            : shouldSkipCompanyStep
              ? (activeStep === (isAdminRole ? 4 : 3))
              : (activeStep === (isAdminRole ? 5 : 4))
        ) && (
            <div className="w-full px-4">
              <ReviewSubmitStep
                onPrevious={() => setActiveStep(id ? 2 : (shouldSkipCompanyStep ? (isAdminRole ? 3 : 2) : (isAdminRole ? 4 : 3)))}
                skipCompany={shouldSkipCompanyStep}
                slackSelected={slackSelected}
                jiraSelected={jiraSelected}
              />
            </div>
          )}

        {/* Hide the old program form completely to unify UI for create/edit */}
        {false && (
          <></>
        )}
        {/* OLD DUPLICATE FORM - do not mount to avoid interfering with RHF values */}
        {false && (
          <div style={{ display: 'none' }}>
            <EditorSection sectionTitle="Program Details">
              {isAdminRole && !id && (
                <EditorInput name="owner_id" inputTitle="Business Owner" required>
                  <FormDropdown
                    name="owner_id"
                    placeholder="Select a Business Owner"
                    options={(usersData?.users || []).map(u => ({
                      value: u.id,
                      label: u.email
                    }))}
                    rules={{ required: true }}
                    className=" m-0 flex w-full flex-col p-0  "
                  />
                  <p className="details mt-1 text-sm text-gray-600">
                    Select the business account to own this program.
                  </p>
                </EditorInput>
              )}
              <EditorTextInput
                name="title"
                inputTitle="Program Name"
                details="Specify the name of this bug bounty (E.G. [Brand Name] Vulnerability Program)."
                placeholder=" "
                required
              />

              <EditorInput name="type" inputTitle="Type" required>
                <FormDropdown
                  name="type"
                  placeholder="Select Program Type"
                  options={[
                    {
                      value: ProgramType.PTAAS,
                      label: "PTaaS"
                    },
                    {
                      value: ProgramType.VDP,
                      label: ""
                    },
                    {
                      value: ProgramType.BugBounty,
                      label: ""
                    }
                  ]}
                  rules={{
                    required: true
                  }}
                  className=" m-0 flex w-full flex-col p-0  "
                  onChangeCallback={value =>
                    setSelectedPogramType(value as ProgramType)
                  }
                />
                <p className="details mt-1 text-sm text-gray-600">
                  Select the program's approach for researchers to process
                  vulnerabilities.
                </p>
              </EditorInput>

              <EditorSection sectionTitle="App Integrations" className="mb-4 mt-4">
                <div className="mb-6 max-w-full overflow-hidden">
                  <div className="mb-6 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-700 p-6 shadow-lg">
                    <h3 className="mb-2 text-xl font-bold text-white">
                      Connect External Services
                    </h3>
                    <p className="mb-0 text-blue-100">
                      Enhance your workflow with seamless third-party integrations
                      for real-time notifications and issue tracking.
                    </p>
                  </div>

                  <div className="mb-6 grid max-w-full grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Slack Integration Card */}
                    <div
                      className={`max-w-full rounded-xl border-2 p-5 ${slackSelected
                        ? "border-green-500 bg-gradient-to-br from-green-50 to-green-100"
                        : "border-gray-200 bg-white"
                        } relative cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-lg`}
                      style={{
                        animation: slackSelected
                          ? "pulse 0.6s cubic-bezier(0.4, 0, 0.6, 1)"
                          : "none"
                      }}
                      onClick={() => {
                        // Toggle the selection when the card is clicked
                        const newValue = !slackSelected;
                        setSlackSelected(newValue);

                        // Update notification methods
                        setNotificationMethods(prevMethods => {
                          const methods = [...prevMethods] as String[];
                          // Always include CTB
                          if (!methods.includes(NotificationMethods.CTB)) {
                            methods.push(NotificationMethods.CTB);
                          }

                          // Add or remove Slack
                          if (
                            newValue &&
                            !methods.includes(NotificationMethods.SLACK)
                          ) {
                            methods.push(NotificationMethods.SLACK);
                          } else if (!newValue) {
                            return methods.filter(
                              m => m !== NotificationMethods.SLACK
                            ) as String[];
                          }

                          return methods;
                        });
                      }}
                    >
                      {/* Animated Corner Ribbon */}
                      <div
                        className={`absolute right-0 top-0 transition-transform duration-500 ease-in-out ${slackSelected ? "translate-x-0" : "translate-x-16"
                          }`}
                        style={{ transitionDelay: slackSelected ? "0.1s" : "0s" }}
                      >
                        <div className="flex h-8 w-8 translate-x-[-16px] translate-y-[-16px] rotate-45 transform items-center justify-center bg-green-500 text-white shadow-md">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 -rotate-45 transform transition-opacity duration-300 ${slackSelected ? "opacity-100" : "opacity-0"
                              }`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-green-400 to-green-600 text-white shadow-md">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold">Slack</h3>
                            <p className="text-sm text-gray-600">
                              Real-time notifications in your workspace
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Jira Integration Card */}
                    <div
                      className={`max-w-full rounded-xl border-2 p-5 ${jiraSelected
                        ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100"
                        : "border-gray-200 bg-white"
                        } relative cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-lg`}
                      style={{
                        animation: jiraSelected
                          ? "pulse 0.6s cubic-bezier(0.4, 0, 0.6, 1)"
                          : "none"
                      }}
                      onClick={() => {
                        // Toggle the selection when the card is clicked
                        const newValue = !jiraSelected;
                        setJiraSelected(newValue);

                        // Update notification methods
                        setNotificationMethods(prevMethods => {
                          const methods = [...prevMethods] as String[];
                          // Always include CTB
                          if (!methods.includes(NotificationMethods.CTB)) {
                            methods.push(NotificationMethods.CTB);
                          }

                          // Add or remove Jira
                          if (
                            newValue &&
                            !methods.includes(NotificationMethods.JIRA)
                          ) {
                            methods.push(NotificationMethods.JIRA);
                          } else if (!newValue) {
                            return methods.filter(
                              m => m !== NotificationMethods.JIRA
                            ) as String[];
                          }

                          return methods;
                        });
                      }}
                    >
                      {/* Animated Corner Ribbon */}
                      <div
                        className={`absolute right-0 top-0 transition-transform duration-500 ease-in-out ${jiraSelected ? "translate-x-0" : "translate-x-16"
                          }`}
                        style={{ transitionDelay: jiraSelected ? "0.1s" : "0s" }}
                      >
                        <div className="flex h-8 w-8 translate-x-[-16px] translate-y-[-16px] rotate-45 transform items-center justify-center bg-blue-500 text-white shadow-md">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 -rotate-45 transform transition-opacity duration-300 ${jiraSelected ? "opacity-100" : "opacity-0"
                              }`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-md">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-6 w-6"
                          >
                            <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z"></path>
                            <path d="M12 12l8-4.5"></path>
                            <path d="M12 12v9"></path>
                            <path d="M12 12L4 7.5"></path>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">Jira</h3>
                          <p className="text-sm text-gray-600">
                            Seamless issue tracking integration
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-blue-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm text-gray-700">
                        You can select multiple integrations for your program. Each
                        integration requires specific configuration.
                      </p>
                    </div>
                  </div>
                </div>
              </EditorSection>

              {slackSelected && (
                <EditorSection sectionTitle="Slack Configuration" className="mt-6">
                  <div className="overflow-hidden rounded-xl border-2 border-green-200 bg-white p-6 shadow-md">
                    <div className="mb-5 flex items-center border-b border-gray-100 pb-4">
                      <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-green-400 to-green-600 text-white shadow-md">
                        <FaSlack className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Slack Integration
                        </h3>
                        <p className="text-gray-600">
                          Configure real-time notifications for your Slack workspace
                        </p>
                      </div>
                    </div>

                    <div className="mb-6 rounded-lg border border-green-100 bg-gradient-to-r from-green-50 to-emerald-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-6 w-6 text-green-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="font-medium text-green-800">
                            Integration Guide
                          </h4>
                          <p className="mt-1 text-sm text-gray-700">
                            To integrate with Slack, you'll need to create an
                            incoming webhook for your channel. Visit the{" "}
                            <a
                              href="https://api.slack.com/messaging/webhooks"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-green-600 underline hover:text-green-800"
                            >
                              Slack API documentation
                            </a>{" "}
                            to learn how.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid max-w-full grid-cols-1 gap-6">
                      {/* Slack Webhook URL Field with custom styling */}
                      <div className="max-w-full overflow-hidden rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-5 shadow-md transition-all duration-300 hover:shadow-lg">
                        <EditorTextInput
                          name="slack_channel_link"
                          inputTitle="Slack Webhook URL"
                          details="Enter your Slack webhook URL to receive notifications"
                          placeholder="https://hooks.slack.com/services/XXXXX/XXXXX/XXXXX"
                          required={slackSelected}
                        />
                        <p className="mt-2 flex items-center text-sm text-green-700">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1 h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          The incoming webhook URL from your Slack workspace
                          settings
                        </p>
                      </div>
                    </div>
                  </div>
                </EditorSection>
              )}

              {jiraSelected && (
                <EditorSection sectionTitle="Jira Configuration" className="mt-6">
                  <div className="max-w-full overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-4 flex items-center border-b border-gray-100 pb-3">
                      <div className="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
                        <SiJira className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              Jira Integration
                            </h3>
                            <p className="text-sm text-gray-600">
                              Connect your project with Jira for issue tracking
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowJiraGuide(true)}
                            className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-100"
                          >
                            <FiHelpCircle className="h-4 w-4" />
                            Setup Guide
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="max-w-full space-y-6">
                      {/* Jira Site URL Field */}
                      <div>
                        <EditorTextInput
                          name="jira_url"
                          inputTitle="Jira Site URL"
                          details="The base URL for your Jira site (e.g., https://company.atlassian.net)"
                          placeholder="https://yourdomain.atlassian.net"
                          required={jiraSelected}
                        />
                      </div>

                      {/* Jira Email Field */}
                      <div>
                        <EditorTextInput
                          name="jira_email"
                          inputTitle="Jira Email"
                          details="The email associated with your Jira account"
                          placeholder="email@example.com"
                          required={jiraSelected}
                        />
                      </div>

                      {/* Jira API Token Field */}
                      <div>
                        <EditorTextInput
                          name="jira_api_token"
                          inputTitle="Jira API Token"
                          details="Your Jira API token for authentication"
                          placeholder="Enter your Jira API token"
                          required={jiraSelected}
                        />
                      </div>

                      {/* Jira Project Key Field */}
                      <div>
                        <EditorTextInput
                          name="jira_project_key"
                          inputTitle="Jira Project Key"
                          details="The project key for your Jira project (e.g., PROJECT)"
                          placeholder="e.g., PROJECT"
                          required={jiraSelected}
                        />
                      </div>
                    </div>

                    {/* Test Connection Section */}
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <div className="mb-4 flex items-center justify-between">
                        <h4 className="text-lg font-semibold text-gray-900">
                          Connection Test
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            const form = document.querySelector("form");
                            if (form) {
                              const formData = new FormData(form);
                              const data = {
                                jira_url: formData.get("jira_url") as string,
                                jira_email: formData.get("jira_email") as string,
                                jira_api_token: formData.get(
                                  "jira_api_token"
                                ) as string,
                                jira_project_key: formData.get(
                                  "jira_project_key"
                                ) as string
                              };
                              testJiraConnection(data);
                            }
                          }}
                          disabled={testing}
                          className={`rounded-lg px-6 py-2 font-medium transition-all duration-200 ${testing
                            ? "cursor-not-allowed bg-gray-400 text-white"
                            : "transform bg-blue-500 text-white hover:scale-105 hover:bg-blue-600 hover:shadow-lg"
                            }`}
                        >
                          {testing ? (
                            <span className="flex items-center">
                              <svg
                                className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Testing...
                            </span>
                          ) : (
                            "Test Connection"
                          )}
                        </button>
                      </div>

                      {/* Test Results */}
                      {jiraTestResult && (
                        <div
                          className={`max-w-full overflow-hidden rounded-lg border p-4 ${jiraTestResult.success
                            ? "border-green-200 bg-green-50"
                            : "border-red-200 bg-red-50"
                            }`}
                        >
                          <div className="flex items-start">
                            <div
                              className={`flex-shrink-0 ${jiraTestResult.success
                                ? "text-green-400"
                                : "text-red-400"
                                }`}
                            >
                              {jiraTestResult.success ? (
                                <svg
                                  className="h-5 w-5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="h-5 w-5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="ml-3 flex-1">
                              <h5
                                className={`font-medium ${jiraTestResult.success
                                  ? "text-green-800"
                                  : "text-red-800"
                                  }`}
                              >
                                {jiraTestResult.success
                                  ? "Connection Successful!"
                                  : "Connection Failed"}
                              </h5>
                              {jiraTestResult.success ? (
                                <div className="mt-2">
                                  {jiraTestResult.projectInfo && (
                                    <div className="text-sm text-green-700">
                                      <p>
                                        <strong>Project:</strong>{" "}
                                        {jiraTestResult.projectInfo.name} (
                                        {jiraTestResult.projectInfo.key})
                                      </p>
                                      <p>
                                        <strong>Available Issue Types:</strong>{" "}
                                        {jiraTestResult.projectInfo.issueTypes.join(
                                          ", "
                                        )}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="mt-2">
                                  <p className="text-sm text-red-700">
                                    {jiraTestResult.error}
                                  </p>
                                  {jiraTestResult.suggestions &&
                                    jiraTestResult.suggestions.length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-sm font-medium text-red-700">
                                          Suggestions:
                                        </p>
                                        <ul className="mt-1 list-inside list-disc text-sm text-red-600">
                                          {jiraTestResult.suggestions.map(
                                            (suggestion: string, index: number) => (
                                              <li key={index}>{suggestion}</li>
                                            )
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </EditorSection>
              )}

              {selectedPogramType === ProgramType.BugBounty && (
                <EditorInput name="private" inputTitle="Program Access" required>
                  <FormDropdown
                    name="private"
                    options={[
                      { value: 0, label: "Public", isDisabled: false },
                      { value: 1, label: "Private", isDisabled: false }
                    ]}
                    value={String(privateVisibility ?? 0)}
                    onChangeCallback={value =>
                      setPrivateVisibility(value ? (Number(value) as 0 | 1) : 0)
                    }
                    rules={{ required: true }}
                    className="py-3"
                  />
                  <p className="details mt-1 text-sm text-gray-600">
                    Select the visibility and access type of this program.
                  </p>
                </EditorInput>
              )}

              <EditorRichTextInput
                name="description"
                inputTitle="Description"
                details="Provide a description about the company and the program."
                required
                minHeight="300px"
              />
            </EditorSection>
          </div>
        )}

        {/* OLD DUPLICATE FORM PART 2 - do not mount to avoid interfering with RHF values */}
        {false && (
          <div style={{ display: 'none' }}>
            <EditorSection sectionTitle="Attachments" className="mt-6">
              {/* Targets input removed in favor of selecting assets from inventory */}

              <ProgramEditorAttachments
                name="attachments"
                inputTitle="Target Attachments"
                details="Optionally add attachments to this program."
                note="Supports any file type"
                placeholder="No Attachments"
                accept="*" // Allow all files
                multiple={true} // Enable multiple selection
                defaultLinks={program?.attachments || []}
              />

              <EditorRichTextInput
                name="vpn"
                inputTitle="VPN Details"
                details="Provide information about any required VPNs (virtual private networks) to access the target assets"
              />

              <EditorRichTextInput
                name="credentials"
                inputTitle="Credentials"
                details="Provide necessary access information for testing the target assets (if applicable)"
              />
            </EditorSection>

            <EditorSection sectionTitle="Scope Description" className="mt-6">
              <EditorRichTextInput
                name="scope"
                inputTitle="Scope"
                details="For a thorough understanding of the testing scope and assets, please provide the necessary documentation and any relevant walkthrough video links."
                required
                minHeight="400px"
              />

              {!!(selectedPogramType !== ProgramType.PTAAS) && (
                <EditorRichTextInput
                  name="outOfScope"
                  inputTitle="Out of Scope"
                  details="Provide details about the digital assets that are NOT open for examination and reporting"
                  required
                />
              )}

              {!!(selectedPogramType !== ProgramType.PTAAS) && (
                <EditorRichTextInput
                  name="knownVulnerabilities"
                  inputTitle="Known Vulnerabilities"
                  details="Provide details about any addressed vulnerabilites (from previous experience or approved reports)"
                />
              )}
            </EditorSection>

            <EditorSection
              sectionTitle={
                selectedPogramType === ProgramType.PTAAS
                  ? "Service Level Agreement"
                  : "Rewards"
              }
              className="mt-6"
            >
              {!!(selectedPogramType === ProgramType.BugBounty) && (
                <ProgramRewardsInput
                  name="rewards"
                  inputTitle="Reward Tiers"
                  required
                />
              )}

              {!!(selectedPogramType === ProgramType.BugBounty) && (
                <EditorRichTextInput
                  name="rewardPolicy"
                  inputTitle="Reward Policy"
                  details="Provide rules and guidelines outlining how researchers are compenstated for discovering and responsibly disclosing vulnerabilities"
                  required
                  minHeight="300px"
                />
              )}

              <EditorRichTextInput
                name="termsOfService"
                inputTitle={
                  selectedPogramType === ProgramType.PTAAS
                    ? "Service Level Agreement"
                    : "Terms and Conditions"
                }
                details={
                  selectedPogramType === ProgramType.PTAAS
                    ? "SLA will be automatically updated once the program is approved by our admins"
                    : "Provide an outline of the terms and conditions of how this program will be conducted"
                }
                required={selectedPogramType !== ProgramType.PTAAS}
                disabled={selectedPogramType === ProgramType.PTAAS}
                minHeight="300px"
              />
            </EditorSection>

            <EditorSection sectionTitle="Additional Information" className="mt-6">
              {!!(selectedPogramType === ProgramType.PTAAS) && (
                <DropDownEditor
                  name="testingType"
                  inputTitle="Testing Type"
                  placeholder="Select Testing Type"
                  options={testingTypeOptions}
                  value=""
                  required
                />
              )}
              {!!(selectedPogramType === ProgramType.PTAAS) && (
                <DropDownEditor
                  name="environmentType"
                  inputTitle="Environment Type"
                  placeholder="Select Environment Type"
                  options={environmentTypeOptions}
                  value=""
                  required
                  className=" mt-5 !bg-ctb-blue-600"
                />
              )}
              {!!(selectedPogramType === ProgramType.PTAAS) && (
                <div className="flex w-full gap-6">
                  <DatePicker
                    name="expectedStartDate"
                    inputTitle="Expected Start Date"
                    required
                  />

                  <DatePicker
                    name="expectedEndDate"
                    inputTitle="Expected End Date"
                    required
                  />
                </div>
              )}
              {!!(selectedPogramType === ProgramType.PTAAS) && (
                <DropDownEditor
                  name="complianceType"
                  placeholder="Select Compliance Type"
                  inputTitle="Compliance Type"
                  options={complianceTypeOptions}
                  value=""
                  required
                  onChangeCallback={value => {
                    setSelectedComplianceType(value);
                  }}
                />
              )}
              {!!(selectedPogramType === ProgramType.PTAAS) &&
                selectedComplianceType === complianceTypes.Other && (
                  <EditorTextInput
                    name="otherComplianceType"
                    inputTitle="Specify Compliance Type"
                    details="Mention the type of compliance, mention NA if not applicable"
                    placeholder=""
                  />
                )}

              <EditorRichTextInput
                name="other"
                inputTitle="Other"
                details="Provide any essential information that has not been disclosed"
              />

              <ProgramEditorLogo
                name="profilePicture"
                inputTitle="Logo Image"
                details="Optionally add a logo image to this program."
                note="Only support .jpg, .png"
                placeholder="No Logo"
                accept="image/*"
                cropImage={{
                  title: "New Logo",
                  cropSize: {
                    width: 300,
                    height: 300
                  },
                  previewSize: {
                    width: 330,
                    height: 390
                  },
                  minZoom: 50,
                  maxZoom: 300
                }}
                defaultLinks={([program?.profilePicture].filter(Boolean) as string[])}
              />
            </EditorSection>
          </div>
        )}

        {/* Hidden component for form-dependent functionality */}
        <div style={{ display: 'none' }}>
          <ProgramEditorFormWrapper />
        </div>
      </EditorLayout>

      {/* Jira Setup Guide Modal */}
      <JiraSetupGuideModal
        isOpen={showJiraGuide}
        onClose={() => setShowJiraGuide(false)}
      />
    </div>
  );
};

// Main component with error boundary
const ProgramEditor = () => {
  const { id } = useParams();

  return (
    <ProgramEditorErrorBoundary
      programId={id}
      onError={(error, errorInfo) => {
        console.error('ProgramEditor Error:', error, errorInfo);
      }}
    >
      <ProgramEditorContent />
    </ProgramEditorErrorBoundary>
  );
};

export default ProgramEditor;
